// Every visible string of the editor, in English.
//
// The mirror image of de.ts: same shape, same keys, same placeholders. German
// is the source — a key is born there, and tsc refuses this file the moment it
// misses one (`en: typeof de`), so the two can never drift apart silently.
//
// Tone follows the German and the English landing page (docs/BRAND.md): second
// person, curly quotes “…”, em dash —. British spelling (colour, vectorise,
// centre). Terms: layer, path, node, vectorise (never "trace"), re-vectorise,
// weld, fill, stroke, crop, brush, eraser. Device and format names are not
// translated.

import type { de } from './de'

export const en: typeof de = {
  common: {
    cancel: 'Cancel',
    close: 'Close',
    confirm: 'Confirm',
    save: 'Save',
    delete: 'Delete',
    done: 'Done',
    dismiss: 'Dismiss',
    retry: 'Try again',
    reload: 'Reload',
    upgradeToPro: 'Upgrade to Pro',
    or: 'or',
    aiRequestsLeft: '{remaining} of {limit} AI requests left this month',
    aiRequestsLeftWithCredits: '{remaining} of {limit} AI requests left this month · Credits: {credits}',
  },

  save: {
    saved: 'Saved',
    saving: 'Saving…',
    dirty: 'Unsaved changes',
    conflict: 'Not saved — conflict',
    error: 'Not saved — error',
    offline: 'Offline — will be saved as soon as you are back online',
    // Shorter, because on a phone this sits under the name as a subtitle.
    shortSaved: 'Saved',
    shortSaving: 'Saving…',
    shortDirty: 'Unsaved',
    shortConflict: 'Conflict',
    shortError: 'Not saved',
    shortOffline: 'Offline',
  },

  topbar: {
    projects: 'Projects',
    backToProjects: 'Back to your projects',
    projectName: 'Project name',
    undo: 'Undo',
    redo: 'Redo',
    zoomIn: 'Zoom in',
    zoomOut: 'Zoom out',
    zoom: 'Zoom',
    zoomTo100: 'Zoom to 100 %',
    fitToView: 'Fit to view',
    shortcuts: 'Keyboard shortcuts',
    history: 'Versions…',
    hidePanels: 'Hide the side panel',
    showPanels: 'Show the side panel',
    rulers: 'Rulers',
    publish: 'Publish',
    updateTemplate: 'Update template',
    viewTemplate: 'View',
    // The "Suggest" button next to the name field (Task 3): an AI name
    // suggestion the user still has to confirm.
    suggestName: 'Suggest a name',
    suggestNamePro: 'Name suggestions are a Pro feature.',
    suggestNameLimit: 'You have reached the daily limit for AI images. It carries on tomorrow.',
    suggestNameUnavailable: 'The name suggestion is unavailable right now. Try again later.',
    suggestNameNoImage: 'Without an image there is nothing to suggest.',
    suggestNameFailed: 'The name suggestion failed. Try again.',
    // Tooltip: "Undo · Ctrl/⌘+Z", title: "Undo (Ctrl/⌘+Z)".
    tip: '{label} · {hint}',
    title: '{label} ({hint})',
  },

  sidebar: {
    tabsLabel: 'Panel groups',
    tabs: {
      ebenen: 'Layers',
      bearbeiten: 'Edit',
      bild: 'Image',
      export: 'Export',
    },
  },

  editSections: {
    pfadForm: 'Path & shape',
    musterEffekte: 'Patterns & effects',
    anordnenBogen: 'Arrange & sheet',
    aufraeumen: 'Clean up',
  },

  tools: {
    select: 'Select',
    hand: 'Hand',
    measure: 'Measure',
    knife: 'Knife',
    freehandKnife: 'Freehand knife',
    node: 'Edit path',
    eyedropper: 'Eyedropper',
    bucket: 'Paint bucket',
    autofill: 'Smart fill',
    brush: 'Brush',
    eraser: 'Eraser',
    crop: 'Crop',
    rect: 'Rectangle',
    roundRect: 'Rounded rectangle',
    ellipse: 'Ellipse',
    line: 'Line',
    polygon: 'Polygon / star',
    heart: 'Heart',
    arrow: 'Arrow',
    banner: 'Banner',
    frame: 'Frame',
    pen: 'Pen',
    pencil: 'Pencil',
    addText: 'Add text',
    railLabel: 'Tools',
    activeColor: 'Active colour',
  },

  shortcuts: {
    dialogTitle: 'Keyboard shortcuts',
    groups: {
      tools: 'Tools',
      view: 'View',
      edit: 'Edit',
      selection: 'Selection',
      shape: 'Shape',
      nodes: 'Nodes',
      bitmap: 'Bitmap',
      help: 'Help',
    },
    keys: {
      mod: 'Ctrl/⌘',
      shift: 'Shift',
      alt: 'Alt',
      space: 'Space',
      delete: 'Del',
      backspace: 'Backspace',
      enter: 'Enter',
      escape: 'Esc',
    },
    labels: {
      holdToPan: 'Hold to pan',
      zoomIn: 'Zoom in',
      zoomOut: 'Zoom out',
      zoomTo100: 'Zoom to 100 %',
      zoomToFit: 'Fit the page into the view',
      undo: 'Undo',
      redo: 'Redo',
      copySelection: 'Copy the selected paths',
      pasteClipboard: 'Paste into the active layer',
      duplicateSelection: 'Duplicate the selection',
      flipHorizontal: 'Flip the selection horizontally',
      flipVertical: 'Flip the selection vertically',
      bringForward: 'Bring the selection forward one step',
      sendBackward: 'Send the selection back one step',
      bringToFront: 'Bring the selection to the front (of its layer)',
      sendToBack: 'Send the selection to the back (of its layer)',
      selectAll: 'Select every visible path',
      nudge: 'Nudge — 0.5 mm, 5 mm with Shift',
      deleteSelection: 'Delete — the chosen nodes in the path editor, the paths otherwise',
      cancel: 'Back to the arrow — and outside the path editor, clear the selection',
      finishEdit: 'Leave the path editor — or apply the crop while cropping is active',
      stepBrushDown: 'Brush a tenth smaller',
      stepBrushUp: 'Brush a tenth larger',
      toggleRulers: 'Show or hide the rulers',
      toggleGrid: 'Show or hide the grid',
      toggleSnap: 'Turn snapping on or off',
      showShortcuts: 'Show this list',
      showHistory: 'Show the versions',
      uniteSelection: 'Unite the selected shapes',
      subtractSelection: 'Subtract the other shapes from the one selected first',
      intersectSelection: 'Intersection of the selected shapes',
      excludeSelection: 'Exclude the overlap',
      groupSelection: 'Group the selection (attach)',
      ungroupSelection: 'Ungroup',
    },
    note: 'Shortcuts are off while you are typing into a field. The mouse has two of its own: a double click opens a shape for node editing, and Alt while dragging a Bézier handle moves only that handle.',
  },

  // The coachmark tour on the first visit to the editor (tour.ts, TourOverlay.tsx).
  // Object groups (attach): Ctrl+G in the editor, see objectGroups.ts.
  objectGroups: {
    name: 'Group',
    tooFew: 'Select at least two objects to group them.',
    ungrouped: 'Ungrouped.',
  },

  // Artboards: several sheets in one project (artboards.ts, ArtboardSwitcher.tsx).
  artboards: {
    title: 'Artboards',
    hint: 'Several sheets in one project — front and back, sizes, variants. Each artboard is exported on its own.',
    unnamed: 'Main artboard',
    primaryBadge: 'Main',
    switchTo: 'Show artboard “{name}”',
    add: 'Add artboard',
    addPromptTitle: 'New artboard',
    addPromptLabel: 'Artboard name',
    addConfirm: 'Create',
    defaultName: 'Artboard {n}',
    limit: 'At most {max} artboards per project.',
    noSize: 'The project has no size yet — upload an image or retrace first.',
    rename: 'Rename artboard',
    renamePromptTitle: 'Rename artboard',
    renamePromptLabel: 'Artboard name',
    renameConfirm: 'Rename',
    duplicate: 'Duplicate artboard',
    delete: 'Delete artboard',
    deleteConfirmTitle: 'Delete artboard',
    deleteConfirmBody: 'Deleting “{name}” deletes every layer on it. This cannot be undone.',
    deleteConfirm: 'Delete artboard',
    makePrimary: 'Make main artboard',
    makePrimaryHint: 'The main artboard is what the preview, the template and the share link show; its size is the project’s.',
    exportNote: 'This exports the artboard “{name}”. Export the other artboards one by one, each from its own tab.',
    publishNote: 'This publishes the artboard “{name}” — a template is one motif.',
    retraceNote: 'Retracing replaces the main artboard; the other artboards stay as they are.',
  },

  tour: {
    start: 'Start the tour',
    progress: 'Step {step} of {total}',
    next: 'Next',
    prev: 'Back',
    finish: 'Done',
    skip: 'Skip',
    toolbar: {
      title: 'Your tools',
      body: 'This is where you pick what you are working with — select, pen, brush and the rest. The shortcut always stands next to it.',
    },
    layers: {
      title: 'Layers',
      body: 'Every colour of your motif is a layer of its own. Here you show and hide them, rename them and sort them.',
    },
    edit: {
      title: 'Edit',
      body: 'Smooth paths, unite shapes, lay out a sheet — everything that changes the vector itself lives here.',
    },
    image: {
      title: 'Image',
      body: 'Retouch the source image or vectorise it again with different settings — that is what this tab is for.',
    },
    export: {
      title: 'Export',
      body: 'When your cut is ready: this is where you download it for your machine.',
    },
  },

  editor: {
    loading: 'Loading the project…',
    loadError: 'The project could not be loaded.',
    bundleError: 'The editor could not be loaded. Please reload the page.',
    reloadError: 'The project could not be reloaded.',
    conversionFailedNoDocument: 'This vectorisation failed. Vectorise the image again to have another go.',
    conversionStatus: 'Vectorisation status: {status}',
    conversionFailed: 'The last vectorisation failed. What you see is the previous result.',
    conversionRunning: 'A vectorisation is running ({status}). This view updates as soon as it is done.',
    retraceNotice: 'The vector layers have been replaced by the new vectorisation.',
    conflict: 'The project was changed elsewhere — reloading discards everything you changed here since the last save.',
    saveError: 'Your changes could not be saved. They are still here, but only in this tab.',
    offlineBanner: 'You are offline. You can carry on editing — it will be saved as soon as the connection is back.',
    offlineLoaded: 'Opened without a connection: this is the state this device saw last.',
    offlineLoadError: 'You are offline, and this project has never been opened on this device.',
    emptyTitle: 'This document has no layers',
    emptyBody: 'Upload an image or vectorise again — the “Re-vectorise” panel vectorises the image under the canvas once more and fills the layers.',
    cutPrompt: 'Did the cut work out?',
    cutPromptAction: 'Write a cut report',
  },

  layers: {
    title: 'Layers',
    empty: 'No layers yet. Upload an image or vectorise again.',
    paths: { one: '1 path', other: '{count} paths' },
    nodes: { one: '1 node', other: '{count} nodes' },
    reorder: 'Move {name}',
    dragToReorder: 'Drag to reorder',
    visibilityTitle: '{action} — Alt-click for solo',
    show: 'Show',
    hide: 'Hide',
    showLayer: 'Show {name}',
    replaceColor: 'Replace {color}',
    colorOf: 'Colour of {name}',
    rename: 'Rename {name}',
    roleTitle: 'Switches between engraving (fill) and cutting (stroke)',
    roleFill: 'Engrave',
    roleStroke: 'Cut',
    lineStyleOf: 'Line style of {name}',
    lineStyleTitle: 'How the lines of this cut layer are cut',
    lineStyleLabel: 'Line style: {name}',
    lineStyleSolid: 'Solid',
    lineStyleDashed: 'Dashed (fold)',
    lineStylePerforated: 'Perforated',
    mergeInto: 'Merge {name} into',
    mergeTitle: 'Merge this layer into another one',
    weldIn: 'Weld the shapes in {name}',
    invertIn: 'Invert {name} (negative)',
    invertTitle: 'Negative: a frame 5 mm around the motif, the motif becomes the hole — the stencil',
    inverted: '{name} inverted',
    weldTitle: 'Weld the overlapping shapes in this layer',
    welded: '{name} welded',
    editText: 'Edit text',
    editTextOf: 'Edit the text of {name}',
    duplicate: 'Duplicate layer',
    duplicateLayer: 'Duplicate {name}',
    delete: 'Delete layer',
    deleteLayer: 'Delete {name}',
    deleteConfirmTitle: 'Delete layer',
    deleteConfirmBody: 'Deleting “{name}” deletes the {paths} in it too.',
    deleteConfirm: 'Delete layer',
    // What a duplicate is called: "Red copy", the way the big programs do it.
    copySuffix: '{name} copy',
    add: 'New layer',
    addTitle: 'Create an empty layer above the active one',
    addName: 'Layer {n}',
    limit: 'No more layers fit into one document.',
    // Groups: a label on the layer under which the list gathers neighbouring
    // layers of the same name.
    group: 'Group',
    groupTitle: 'Put the active layer into a group',
    groupPromptTitle: 'Group layers',
    groupPromptLabel: 'Name of the group',
    groupToast: { one: '1 layer in “{group}”', other: '{count} layers in “{group}”' },
    groupAssign: 'Group of {name}',
    groupAssignTitle: 'Put this layer into a group',
    groupNone: 'Take out of the group',
    groupCount: { one: '1 layer', other: '{count} layers' },
    groupedPaths: { one: '1 path in an object group', other: '{count} paths in object groups' },
    groupCollapse: 'Collapse the group {name}',
    groupExpand: 'Expand the group {name}',
    groupVisibility: 'Show or hide the group {name}',
    ungroup: 'Ungroup',
    ungroupOf: 'Ungroup {name}',
    sortByColor: 'Sort by colour',
    sortByColorTitle: 'Group layers of the same colour together',
    mergeAdjacent: 'Join same-colour neighbours',
    mergeAdjacentTitle:
      'Make neighbouring layers of one colour a single layer – reordering and recolouring do this on their own',
    // Isolate: show it alone and make it active at the same time, so a brush
    // stroke or a paste cannot land on a neighbour nobody can see.
    isolate: 'Isolate layer',
    isolateOf: 'Isolate {name}',
    isolateTitle: 'Show only this layer and make it the active one',
    exitIsolation: 'End isolation',
    exitIsolationOf: 'End isolation of {name}',
    isolationBanner: 'Isolated: {name}',
    isolationBannerExit: 'End',
  },

  colorPicker: {
    title: 'Choose a colour',
    plane: 'Saturation and brightness',
    hue: 'Hue',
    hex: 'Hex code',
  },
  colors: {
    title: 'Colours',
    empty: 'This document has no layers yet.',
    use: 'Use {color} ({layer})',
    swatchTitle: '{layer} — {color}. Double-click to replace.',
    replace: 'Replace {color}',
    mergeSimilar: 'Merge similar ones',
    threshold: 'Threshold',
    thresholdLabel: 'Threshold for colour similarity',
    thresholdOff: 'off',
    thresholdFine: 'fine',
    thresholdMedium: 'medium',
    thresholdCoarse: 'coarse',
    modesLegend: 'What should happen to similar colours',
    modeMerge: 'Merge layers',
    modeRecolor: 'Only recolour',
    noneClose: 'No two colours are that close to each other.',
    dragHint: 'Drag the slider to gather nearly identical colours into one.',
    outcomeMerge: '{before} → {after}',
    outcomeRecolor: '{before} → {after}, layers stay',
    layers: { one: '1 layer', other: '{count} layers' },
    colours: { one: '1 colour', other: '{count} colours' },
    mergeButton: 'Merge {layers}',
    recolorButton: 'Recolour {layers}',
    mergedToast: '{removed} merged into {groups}.',
    collisionTitle: 'Two layers, one colour',
    collisionBody: '“{name}” already has {color}. Merge into the existing layer?',
    collisionConfirm: 'Merge layers',
    collisionKeep: 'Keep both',
    recoloredToast: '{changed} recoloured; {colours} of them remain.',
    foils: { one: '1 foil', other: '{count} foils' },
    mapToFoils: 'Map to my foils',
    mapToFoilsHint: 'Every layer colour gets the closest colour from your own foil palette.',
    mapToFoilsUpgrade: 'Mapping to your foils is a Pro feature.',
    mapToFoilsEmpty: 'Add some foil colours to your account first.',
    mapToFoilsEmptyLink: 'Go to my foils',
    mapToFoilsToast: 'Mapped {layers} to {foils}.',
  },

  path: {
    title: 'Path',
    empty: 'Select a path — or double-click one — to simplify or smooth it.',
    nodeCount: '{count} nodes',
    nodeCountPreview: '{before} → {after} nodes',
    inPaths: ' in {count} paths',
    kept: 'Shape kept — no adjustment stays within this tolerance.',
    simplify: 'Simplify',
    simplifyTolerance: 'Tolerance for simplifying',
    smooth: 'Smooth',
    smoothStrength: 'Smoothing strength',
    round: 'Round the corners',
    roundRadius: 'Radius for corner rounding',
    roundApply: 'Apply rounding',
    roundTooComplex: {
      one: '1 path was too complex to round.',
      other: '{count} paths were too complex to round.',
    },
  },

  // Decorative edge: the section below "Round the corners" in the path panel.
  decoEdge: {
    title: 'Decorative edge',
    kind: 'Style',
    kindLabel: 'Style of the decorative edge',
    kindZigzag: 'Zigzag',
    kindWave: 'Waves',
    kindScallop: 'Scallops',
    kindStamp: 'Postage stamp',
    amplitude: 'Depth',
    amplitudeLabel: 'Depth of the decorative edge',
    wavelength: 'Wavelength',
    wavelengthLabel: 'Wavelength of the decorative edge',
    direction: 'Direction',
    directionLabel: 'Direction of the decorative edge',
    directionOutward: 'Outward',
    directionInward: 'Inward',
    directionFixed: 'A postage stamp always notches inward.',
    apply: 'Apply the decorative edge',
    empty: 'Select a closed path to decorate its edge.',
    previewOff: 'Too many nodes for a preview — “Apply” still runs.',
    tooComplex: {
      one: '1 path grew too large for the decorative edge.',
      other: '{count} paths grew too large for the decorative edge.',
    },
    helpTitle: 'Decorative edge',
    helpBody:
      'Decorates the edge of the selected closed paths with a zigzag, waves, scallops or a postage-stamp perforation — running on around every corner. The wavelength is nudged slightly so that a whole number of periods fits the perimeter and the pattern closes seamlessly at the start point.',
  },

  // Arrange: align, distribute, measurements; rulers, grid, guides, snapping.
  arrange: {
    title: 'Arrange',
    align: 'Align',
    reference: 'Relative to',
    referenceSelection: 'Selection',
    referencePage: 'Page',
    left: 'Align left',
    centerX: 'Centre horizontally',
    right: 'Align right',
    top: 'Align top',
    centerY: 'Centre vertically',
    bottom: 'Align bottom',
    order: 'Stacking order',
    toFront: 'Bring to front',
    forward: 'Bring forward one step',
    backward: 'Send back one step',
    toBack: 'Send to back',
    distribute: 'Distribute',
    distributeX: 'Space the centres evenly, horizontally',
    distributeY: 'Space the centres evenly, vertically',
    gapsX: 'Equal gaps horizontally',
    gapsY: 'Equal gaps vertically',
    empty: 'Select a path to align or measure it.',
    hint: 'Aligning to the selection needs two paths, distributing needs three — to the page, one is enough.',
    measures: 'Measurements',
    x: 'X (mm)',
    y: 'Y (mm)',
    width: 'Width (mm)',
    height: 'Height (mm)',
    lockAspect: 'Lock the aspect ratio',
    rotation: 'Rotation (°)',
    rotate: 'Rotate',
    rotationHint: 'Rotates around the anchor point and resets the field to 0.',
    anchor: 'Anchor point',
    anchors: {
      nw: 'Top left',
      n: 'Top centre',
      ne: 'Top right',
      w: 'Middle left',
      c: 'Centre',
      e: 'Middle right',
      sw: 'Bottom left',
      s: 'Bottom centre',
      se: 'Bottom right',
    },
    invalidNumber: 'Please enter a number, 50.5 for example.',
    arrayTitle: 'Duplicate in a pattern',
    arrayCircle: 'In a circle',
    arrayGrid: 'In a grid',
    arrayCount: 'Count',
    arrayRows: 'Rows',
    arrayCols: 'Columns',
    arrayGapMm: 'Gap (mm)',
    arrayRadiusMm: 'Radius (mm)',
    arrayRotate: 'Rotate the copies too',
    arrayEmpty: 'Select a path to duplicate it.',
    arrayTooMany: 'That would push a layer past {max} paths — make fewer copies.',
    view: 'Rulers, grid, snapping',
    rulers: 'Rulers',
    grid: 'Grid',
    gridStep: 'Grid step',
    gridStepValue: '{step} mm',
    snap: 'Snap',
    snapHint: 'Snaps to the grid, to guides, to the page edges and centre, and to other paths. Alt while dragging turns snapping off.',
    guides: 'Guides',
    guideAxis: 'Direction',
    guideVertical: 'Vertical',
    guideHorizontal: 'Horizontal',
    guideAt: 'Guide at (mm)',
    addGuide: 'Add a guide',
    noGuides: 'No guides yet — drag one out of a ruler or add it here.',
    guideRow: '{axis} at {mm}',
    guideSelect: 'Select the {axis} guide at {mm}',
    removeGuide: 'Delete the {axis} guide at {mm}',
    rulerTop: 'Ruler at the top, millimetres',
    rulerLeft: 'Ruler on the left, millimetres',
    rulerUnit: 'mm',
  },

  // Sheet layout: bin-packing motifs onto a mat (nesting.ts,
  // panels/SheetPanel.tsx).
  nesting: {
    title: 'Sheet',
    format: 'Mat size',
    formats: {
      'cricut-12x12': 'Cricut mat 30 × 30 cm',
      'cricut-12x24': 'Cricut mat 30 × 60 cm',
      'silhouette-12x12': 'Silhouette mat 30 × 30 cm',
      'silhouette-12x24': 'Silhouette mat 30 × 60 cm',
      'brother-12x12': 'Brother mat 30 × 30 cm',
      'brother-12x24': 'Brother mat 30 × 60 cm',
      a4: 'A4 (210 × 297 mm)',
      a3: 'A3 (297 × 420 mm)',
      custom: 'Custom size',
    },
    width: 'Width (mm)',
    height: 'Height (mm)',
    margin: 'Margin (mm)',
    spacing: 'Spacing (mm)',
    rotate: 'Allow rotating (90°)',
    arrange: 'Arrange',
    arrangeHint: 'Packs the selection — every visible path if nothing is selected — onto the mat and sets the page to the mat size.',
    copies: 'Copies',
    fillMax: 'As many as fit',
    fill: 'Fill with copies',
    fillHint: 'Duplicates the selected motif and packs every copy onto the mat as well.',
    needSelection: 'Select a motif to duplicate it.',
    invalidNumber: 'Please enter a number, 50.5 for example.',
    invalidSize: 'Width and height have to be between {min} and {max} mm.',
    overflow: {
      one: '1 part no longer fits on the mat and sits next to it, on the right.',
      other: '{count} parts no longer fit on the mat and sit next to it, on the right.',
    },
    nothingFits: 'Not even one copy fits on the mat — pick a larger mat.',
    layerLimit: 'A layer holds {max} paths at most — this makes {count} copies.',
    empty: 'No visible paths — there is nothing to arrange.',
    byColor: 'Sort by colour',
    byColorHint: 'Groups every visible path by the colour of its layer and puts each colour into a band of its own, one below the other.',
    byColorBand: {
      one: '{color}: 1 part, {range}',
      other: '{color}: {count} parts, {range}',
    },
    trueShape: 'True shape (Pro)',
    trueShapeHint: 'Packs silhouettes instead of rectangles — parts may interlock. Takes a moment longer.',
    trueShapePro: 'True-shape nesting is a Pro feature.',
    trueShapeProgress: 'Nesting by contour … {done}/{total}',
    trueShapeFailed: 'True-shape nesting failed.',
    trueShapeChanged: 'The document changed in the meantime — the result was discarded.',
    quality: 'Used area: {after} % by contour instead of {before} % with rectangles.',
  },

  // Weeding aids in the sheet panel: a frame and bridges as a line layer of
  // their own.
  weeding: {
    title: 'Weeding',
    margin: 'Distance (mm)',
    bridges: 'Bridges',
    run: 'Add the aids',
    layerName: 'Weeding',
    hint: 'The frame and the bridges land in a line layer of their own, right at the top — for weeding vinyl; delete it after cutting.',
    empty: 'No visible paths — there is nothing to weed.',
    done: { one: '1 weeding aid added.', other: '{count} weeding aids added.' },
  },

  mailMerge: {
    menu: 'Series…',
    title: 'Create a series',
    helpTitle: 'Series',
    help: 'Write a placeholder in curly braces into a text, for example “Hello {Name}”. Paste a list below — one line per item — and the series makes a copy of the design for every line, with the text filled in, attached as an object group on the sheet.',
    noPlaceholders:
      'No placeholder in this document. Set a text with curly braces, for example “Hello {Name}” — the series then replaces it line by line.',
    placeholders: 'Placeholders found',
    list: 'List',
    listHintOne: 'One line per item.',
    listHintMany: 'One line per item, columns separated by a semicolon, a comma or a tab.',
    listExampleOne: 'Anna\nBo\nCem',
    listExampleMany: 'Anna;3\nBo;7',
    count: { one: '1 item', other: '{count} items' },
    headerFound: 'First line read as a header row: {columns}.',
    scope: 'Scope',
    scopeAll: 'The whole design',
    scopeText: 'Only text layers with placeholders',
    spacing: 'Spacing (mm)',
    keepTemplate: 'Keep the template',
    fits: '{fit} of {total} fit on the sheet — the rest sits next to it.',
    tooMany: 'At most {max} items at a time — the remaining lines are left out.',
    layerLimit: 'A layer holds at most {max} paths — it will be {count} items.',
    empty: 'Enter at least one line.',
    nothingToPlace: 'Nothing to arrange — the scope holds no visible path at all.',
    create: 'Create the series',
    groupName: 'Series',
    done: { one: '1 item created.', other: '{count} items created.' },
    overflow: {
      one: '1 item no longer fits on the sheet and sits next to it.',
      other: '{count} items no longer fit on the sheet and sit next to it.',
    },
    failed: 'The series could not be created.',
    counterHelp:
      'To count along, write {#} into the text — “Table {#}”, say. The series counts it up itself; a list needs no column for it, and both together work too (“{Name}, table {#}”).',
    counter: 'Counter',
    counterStart: 'Start',
    counterStep: 'Step',
    counterDigits: 'Digits (padded with zeros)',
    counterPrefix: 'Before',
    counterSuffix: 'After',
    counterPreview: 'Gives “{first}” through “{last}”.',
    counterPreviewOne: 'Gives “{first}”.',
    counterZeroStep: 'A step of 0 — every copy then carries the same number.',
    copies: 'Count',
    copiesHint: 'Without a list only the counter runs: this many copies are created, at most {max}.',
    tooManyCopies: 'At most {max} copies at once — {max} will be created.',
  },

  cleanup: {
    title: 'Clean up',
    parts: { one: '1 part', other: '{count} parts' },
    smallerThan: '{parts} smaller than {size}',
    smallParts: 'Small parts',
    smallPartSize: 'Size of the small parts',
    // One button per machine sets the slider straight to the threshold the
    // export for that machine already reports (device_export/profiles).
    devicePresets: 'Machine presets',
    devicePreset: 'Preset {name}',
    deviceCricut: 'Cricut',
    deviceXtool: 'xTool',
    deviceBrother: 'Brother',
    remove: 'Remove {parts}',
    totalNodes: 'Nodes in total: {count}',
    optimize: 'Optimise for Cricut (≤ 5,000 nodes)',
    optimizedToast: '{before} → {after} nodes at {tolerance}',
    optimizeFailed: '5,000 nodes not reached; the best possible result has been applied.',
    overlaps: 'Overlaps',
    overlapsHint: 'Shapes that overlap are cut twice. Welding leaves one outline per layer.',
    weld: 'Weld the overlapping shapes',
    weldTitle: 'Welds every layer on the canvas — while a layer is solo, only that one. The checkbox in the export always welds every visible layer.',
    // The plotter doctor on the open document — the same findings and repairs
    // as on an SVG import (svgDoctor.ts).
    doctorClean: 'Nothing to report — this document is ready to cut.',
    doctorRepair: 'Fix everything',
    doctorDone: { one: 'Finding fixed — 1 change.', other: 'Findings fixed — {count} changes.' },
    // The stencil check: parts that would fall out when a stencil is cut (a
    // hole with no bridge to its surroundings). Deliberately without an
    // automatic repair — whether a bridge belongs there is design, not a bug.
    stencilTitle: 'Stencil check',
    stencilNone: 'No loose inner parts',
    stencilFound: { one: '{count} loose inner part', other: '{count} loose inner parts' },
    stencilSkipped: 'Too many paths for the stencil check',
    stencilShow: 'Show',
    stencilHide: 'Hide',
    // The repair next to the check: bridges tying every island to the
    // surrounding material — previewed as an overlay, applied as one undo
    // step.
    bridgeWidth: 'Bridge width (mm)',
    bridgeShow: 'Show bridges',
    bridgeHide: 'Hide bridges',
    bridgeInsert: 'Insert bridges',
    bridgeNone: 'No room for bridges found — please place them by hand.',
    bridgeDone: { one: '1 bridge inserted — no loose parts left.', other: '{count} bridges inserted — no loose parts left.' },
    bridgeLeft: { one: 'Bridges inserted, but 1 part is still loose.', other: 'Bridges inserted, but {count} parts are still loose.' },
  },

  shape: {
    title: 'Shape',
    boolean: 'Boolean',
    booleanHint: 'Select at least two paths. The shape selected first is the target, the others act on it.',
    unite: 'Unite',
    subtract: 'Subtract',
    intersect: 'Intersect',
    exclude: 'Exclude',
    divide: 'Divide',
    uniteTitle: 'Every selected shape into one area',
    subtractTitle: 'Subtracts the other shapes from the one selected first',
    intersectTitle: 'Only the area every selected shape has in common',
    excludeTitle: 'Everything but the overlap',
    divideTitle: 'Breaks the shapes into separate pieces along their overlaps',
    booleanEmpty: 'Empty result — the shapes do not overlap in a way that leaves anything.',
    booleanOpen: 'Open paths cannot be combined. Close them in the path editor first.',
    booleanDone: { one: 'Done — 1 path', other: 'Done — {count} paths' },
    offset: 'Outline / offset',
    offsetEmpty: 'Select one or more closed paths to create an outline.',
    distance: 'Distance',
    distanceLabel: 'Distance of the outline in millimetres',
    distanceInput: 'Distance as a number in millimetres',
    join: 'Corners',
    joinRound: 'Round',
    joinSquare: 'Square',
    joinMiter: 'Mitre',
    outlineOnly: 'Outer contour only (discard holes)',
    target: 'Result',
    targetLayer: 'As a new layer “{name}”',
    targetReplace: 'Replace the path',
    layerColor: 'Colour of the outline layer',
    layerName: 'Outline',
    apply: 'Apply the outline',
    previewOff: 'Preview off — the selection has more than {limit} nodes. “Apply” still runs.',
    offsetEmptyResult: 'Nothing left — the distance is larger than the shape.',
    offsetDone: 'Outline created',
    replaced: { one: '1 path replaced', other: '{count} paths replaced' },
    textHeading: 'Text',
    weldText: 'Weld the letters',
    weldTextTitle: 'Unites every letter of the text layer into one path — holes are kept.',
    frameBorder: 'Frame width',
    warp: 'Warp',
    warpUpgrade: 'Arch, perspective and conical warp (tumblers) come with Pro.',
    warpEmpty: 'Select one or more paths to warp them.',
    warpMode: 'Mode',
    warpModeArch: 'Arch',
    warpModePerspective: 'Perspective',
    warpModeConical: 'Conical (tumbler)',
    warpBend: 'Bend',
    warpBendLabel: 'Bend in percent — positive arches up, negative down',
    warpTop: 'Narrow the top',
    warpTopLabel: 'Top edge narrower in percent; negative widens it',
    warpBottom: 'Narrow the bottom',
    warpBottomLabel: 'Bottom edge narrower in percent; negative widens it',
    warpTopDiameter: 'Ø top (mm)',
    warpBottomDiameter: 'Ø bottom (mm)',
    warpHeight: 'Height (mm)',
    warpConicalHint: 'Maps the selection onto the unrolled cup wall — the selection’s top edge sits at the rim.',
    warpPreset: 'Tumbler preset',
    warpPresetCustom: 'Custom size',
    warpPresetSkinny20: '20 oz skinny tumbler, approx. 71/67/206',
    warpPresetTumbler30: '30 oz tumbler, approx. 88/73/200',
    warpPresetLatte17: 'Latte mug (17 oz), approx. 94/62/118',
    warpSmooth: 'Refit the result with curves',
    warpApply: 'Apply the warp',
    warpDone: { one: '1 path warped', other: '{count} paths warped' },
    warpNoFit: 'The design does not fit this cup — wider than the top circumference or taller than the unrolled wall.',
    knifeDone: { one: 'Cut — 1 path', other: 'Cut — {count} paths' },
    knifeEmpty: 'The line does not cut all the way through a closed shape.',
    knifeHint: 'Select a closed shape, or drag the line across one.',
  },

  // Trapping and knocking out for layered vinyl: geometry/layering.ts,
  // LayeringPanel.tsx.
  layering: {
    title: 'Layering',
    hint: 'Needs at least two visible engrave layers that cover one another.',
    overlap: 'Overlap',
    overlapLabel: 'Overlap in millimetres',
    overlapInput: 'Overlap as a number in millimetres',
    scope: 'Scope',
    scopeActive: 'Active layer only (as the base)',
    scopeAll: 'All layers',
    knockout: 'Knock out',
    knockoutTitle: 'Takes out of every layer whatever the layers above cover — all but a narrow seam at the edge.',
    trap: 'Trap',
    trapTitle: 'Grows every layer by the overlap underneath the layers above it.',
    done: { one: '1 path changed', other: '{count} paths changed' },
    none: 'No overlap found',
    previewOff: 'Preview off — the layers have more than {limit} nodes. The actions still run.',
  },

  // The rhinestone generator (Pro): geometry/rhinestone.ts, RhinestonePanel.tsx.
  rhinestone: {
    title: 'Rhinestones',
    upgrade: 'The rhinestone generator is a Pro feature.',
    hint: 'Select one or more paths to build a rhinestone pattern.',
    size: 'Stone size',
    sizeLabel: 'Stone size as an SS measure',
    sizeCustom: 'Custom…',
    customSize: 'Stone diameter',
    customSizeLabel: 'Stone diameter in millimetres',
    holeNote: 'Hole diameter {hole} — the stone plus {clearance} of play, so it drops into the hole by itself.',
    spacing: 'Minimum spacing',
    spacingLabel: 'Minimum spacing between the holes in millimetres',
    mode: 'Mode',
    modeContour: 'Outline',
    modeFill: 'Fill',
    modeBoth: 'Both',
    grid: 'Grid',
    gridHex: 'Staggered',
    gridSquare: 'Square',
    hideSource: 'Hide the source layers',
    count: { one: '1 stone', other: '{count} stones' },
    truncated: 'Stopped at {limit} stones — a larger stone or more spacing helps.',
    layerName: 'Rhinestones',
    apply: 'Apply rhinestones',
    done: { one: 'Rhinestone layer created — 1 stone', other: 'Rhinestone layer created — {count} stones' },
    empty: 'No stone fits — the shape is too small for this stone size.',
  },

  // The drawing tools: the shapes group in the toolbar, pen, pencil.
  shapes: {
    menu: 'Shapes',
    title: 'Shapes and pen',
    layerName: 'Shapes',
    options: 'Options',
    sides: 'Corners (3–12)',
    innerRadius: 'Inner radius (%)',
    innerRadiusHint: 'At 100 % a regular polygon, below that a star.',
    cornerRadius: 'Corner radius (mm)',
    modifierHint: 'Shift keeps the aspect ratio (square, circle, 45° line), Alt draws from the centre.',
    penHint: 'A click sets a corner, dragging pulls out handles. A click on the starting point closes the path, Enter or Esc ends it open, Backspace takes the last point back.',
  },

  weld: {
    nothing: 'Nothing to weld',
    layers: { one: '1 layer welded', other: '{count} layers welded' },
  },

  invert: {
    nothing: 'Nothing to invert — the layer has no closed shape',
    layers: { one: '1 layer inverted', other: '{count} layers inverted' },
  },

  raster: {
    title: 'Bitmap',
    brush: 'Brush',
    brushSize: 'Brush size',
    brushHint: 'The brush (P) paints in the active colour, the eraser (E) paints white. [ and ] change the size.',
    brushHintLayer:
      'The brush (P) paints shapes onto the active layer, the eraser (E) erases them. [ and ] change the size.',
    target: 'Paint on',
    targetLabel: 'Target of brush and eraser',
    targetLayer: 'Layer',
    targetLayerHint: 'Strokes become shapes on the active layer',
    targetPhoto: 'Photo',
    targetPhotoHint: 'Strokes change the source image for re-vectorising',
    targetPhotoMissing: 'This project has no source image',
    smartFill: 'Smart fill',
    smartFillHint: 'The smart fill (F) takes the area of the image under the pointer and vectorises it into the layer of the active colour. The tolerance says how far a pixel may differ in colour from the one you clicked; “Everywhere” takes that colour across the whole image, not only where it is connected.',
    dragRectangle: 'Drag a rectangle on the canvas.',
    cropSize: '{width} × {height} mm',
    crop: 'Crop',
    cancel: 'Cancel',
    // What the undo entry of a brush stroke is called.
    strokeBrush: 'Brush',
    strokeErase: 'Erase',
    // The layer a brush stroke creates on an empty document.
    layerName: 'Painting',
  },

  autofill: {
    tolerance: 'Tolerance',
    toleranceLabel: 'Tolerance of the fill',
    modeLabel: 'Fill mode',
    contiguous: 'Connected',
    contiguousHint: 'Only the area connected to the point you clicked',
    global: 'Everywhere',
    globalHint: 'Every area of this colour across the whole image',
    tooLargeGlobal: 'This colour is too detailed to fill in one go. Try a smaller tolerance or switch to “Connected”.',
    tooLarge: 'This area is too detailed to fill in one go. Try a smaller tolerance.',
  },

  reference: {
    title: 'Reference',
    originalImage: 'Original image',
    opacity: 'Opacity of the original image',
    mockupsTitle: 'Product mockups',
    mockupBecher: 'Mug',
    mockupTshirt: 'T-shirt',
    mockupKlappkarte: 'Folded card',
    mockupGlas: 'Glass',
    mockupHint: 'Fits a document of {width} × {height}.',
    mockupRemove: 'Remove',
  },

  retrace: {
    title: 'Re-vectorise',
    confirmTitle: 'Vectorise the image again',
    confirmBody: 'Replaces every vector layer. Your path edits will be lost.',
    confirm: 'Re-vectorise',
    button: 'Re-vectorise',
    running: 'Vectorising…',
    noImage: 'This project has no source image — it was opened from a template or created empty. Upload an image to vectorise it.',
    uploadFailed: 'The edited image could not be uploaded. Please try again.',
    started: 'The image is being vectorised again…',
    limitReached: 'Daily limit reached ({used} / {limit}).',
    rejected: 'The server did not accept these settings.',
    failed: 'The vectorisation could not be started. Please try again.',
    failedToast: 'The vectorisation could not be started.',
    unlimited: 'for unlimited vectorisations.',
    // The preview for the sliders: runs at 1024 px, costs no daily slot.
    preview: 'Preview',
    previewHint: 'Shows what the sliders produce – costs no daily slot.',
    previewPending: 'Calculating the preview…',
    previewSummary: 'Preview: {px} px, {colors}, {seconds} s',
    previewColors: { one: '1 colour', other: '{count} colours' },
    previewShowOriginal: 'Show the original',
    previewShowPreview: 'Show the preview',
    previewTimeout:
      'The preview took too long. The sliders stay usable – “Re-vectorise” runs without this limit.',
    previewRejected: 'The server did not accept these settings.',
    previewThrottled: 'Too many previews in a short time. Try again in a few minutes.',
    previewFailed: 'The preview could not be calculated.',
    previewDirty: 'The preview shows the image uploaded last – brush strokes that have not been uploaded are missing.',
    // The colour-count comparison: three previews at half, current and double
    // palette size, side by side and clickable.
    compareTitle: 'Compare colours',
    compareRun: 'Calculate three variants',
    compareUse: 'Take {count} colours',
    compareTileLabel: '{colors} · {layers}',
    compareLayers: { one: '1 layer', other: '{count} layers' },
    compareBusy: 'Another preview is already running. Try again in a few seconds.',
    compareFailed: 'The colour comparison could not be calculated. Please try again.',
    // Touching up a region (Task 8): a rectangle over the image; only that
    // section goes through the vectorisation once more.
    regionTitle: 'Touch up a region',
    regionStart: 'Choose a region',
    regionRun: 'Vectorise the region',
    regionReplace: 'Replace the paths in the region',
    regionHint:
      'Drag a rectangle on the canvas over the spot that should get better – at least 10 mm wide. The run costs a daily slot, like a vectorisation.',
    regionDone: 'The region has been vectorised again.',
    regionFailed: 'The region could not be vectorised again. Please try again.',
    regionOffline: 'Without a connection no region can be vectorised.',
    // Named sets of slider values, personal to an account (Task 9, round 3).
    // Applying only fills the fields, saving records the current state --
    // neither of them starts a vectorisation on its own.
    presetsTitle: 'Presets',
    presetsSelectLabel: 'Apply a preset',
    presetsSelectEmpty: 'No preset saved',
    presetsSelectPlaceholder: 'Choose a preset…',
    presetsSave: 'Save as a preset',
    presetsSaveTitle: 'Save preset',
    presetsSaveLabel: 'Name',
    presetsSaved: 'Preset “{name}” saved.',
    presetsSaveFailed: 'The preset could not be saved.',
    presetsDuplicateName: 'A preset with that name already exists.',
    presetsDelete: 'Delete preset',
    presetsDeleteTitle: 'Delete the preset?',
    presetsDeleteBody: 'It will be deleted for good.',
    presetsDeleted: 'Preset “{name}” deleted.',
    presetsDeleteFailed: 'The preset could not be deleted.',
    // Task 1 "Legacy Cleanup": a preset saved by hand through the API can
    // carry an engine this server does not have (any more) -- `applyTracePreset`
    // swaps it for the first available one when applied, and this toast says
    // what just happened rather than letting the next retrace fail silently
    // with a 422.
    presetEngineDegraded: 'Engine “{from}” is not available here — using “{to}” instead.',
  },

  traceParams: {
    colors: 'Colours',
    filterSpeckle: 'Filter speckles',
    minArea: 'Discard parts smaller than',
    paletteSize: 'Limit the palette to',
    paletteSizeUnit: 'colours',
    paletteSizeOff: 'off',
    paletteSizeAria: 'Limit the palette to this many colours (empty = off)',
    mergeDeltaE: 'Merge colours closer than ΔE',
    mergeDeltaEUnit: 'to each other',
    mergeDeltaEAria: 'Merge colours that are closer to each other than this ΔE value',
    // The one slider potrace, autotrace and skeleton actually read (I4,
    // round four follow-ups): the brightness share above which a pixel
    // counts as drawing rather than paper. vtracer never binarises, so it
    // never shows this slider.
    threshold: 'Threshold',
    thresholdAria: 'Threshold above which a pixel counts as drawing rather than paper',
    mode: 'Mode',
    hierarchy: 'Hierarchy',
    // Splitting layers (Task 8, round three): by colour as before, or by
    // brightness -- the image goes grey before quantisation for that.
    separate: 'Split layers',
    separateColor: 'By colour',
    separateBrightness: 'By brightness',
    engine: 'Engine',
    // Autotrace's `-centerline` mode: a pencil sketch is cut along its centre
    // line instead of as a filled area (Task 7). Only in the list when the
    // server found the binary (`engines` in GET /api/v1/entitlements).
    engineAutotrace: 'Centre line (sketch)',
    // The same idea without a second program (round four, task 5):
    // ImageMagick thins the drawing down to a pixel, and that becomes
    // straight polylines. "simple" is the warning: no curves -- and in
    // exchange the engine is available everywhere.
    engineSkeleton: 'Centre line (simple)',
  },

  export: {
    title: 'Export',
    deviceProfile: 'Machine profile',
    profileCricut: 'Cricut',
    profileXtool: 'xTool laser',
    profileBrother: 'Brother ScanNCut',
    format: 'Format',
    proSuffix: ' — Pro',
    proFormats: 'DXF, PNG, PDF, Brother FCM and xTool XCS are Pro formats.',
    mat: 'Mat',
    mat12x12: '12 × 12 inches (standard mat)',
    mat12x24: '12 × 24 inches (long mat)',
    fcmHint: 'The ScanNCut reads the FCM file straight off a USB stick; the drawing sits in the middle of the mat, every layer is an object of its own. Not tested on a machine yet — tell us if your ScanNCut will not read the file.',
    brotherHint: 'For CanvasWorkspace as SVG in millimetres, for the ScanNCut directly as Brother FCM.',
    xcsHint:
      'xTool Studio opens the XCS file straight away as a project: every layer is an object, cut layers are set to "Cut", engrave layers to "Engrave (fill)" — material, power and speed you set in Studio. Made for the xTool S1; pick another xTool when opening.',
    silhouetteHint: 'Silhouette Studio: the free basic version reads DXF, SVG only from the Designer Edition upwards. There is no .studio3 export — the format is encrypted.',
    width: 'Width (mm)',
    height: 'Height (mm)',
    weldFirst: 'Weld the overlaps before exporting',
    // "Mirror for iron-on" (HTV): the classic beginner mistake -- iron-on
    // vinyl is cut in reverse, and Cricut and Silhouette both ask at exactly
    // this point. Only the export is mirrored (`DeviceExport::Mirror`); the
    // canvas in the editor stays untouched, which is why the hint says both.
    mirrorHtv: 'Mirror for iron-on vinyl (HTV)',
    mirrorHtvHint: 'The motif is exported flipped horizontally — the canvas in the editor stays as it is. Iron-on vinyl (HTV) is cut in reverse; once pressed on, it reads right again.',
    // The mix-up guard after the export: the file that just left the house
    // is mirrored -- not to be confused with a regular cut file.
    mirroredBadge: 'Exported mirrored — ready for iron-on vinyl.',
    button: 'Export',
    exporting: 'Exporting…',
    // Passes the chosen machine profile through since Task 8, instead of
    // always naming Cricut -- the button no longer needs a profile name.
    layersZip: 'ZIP per layer',
    // M13-Rest: its own busy label like the role split and "everything as
    // ZIP" buttons -- this one was the only ZIP/export neighbor without one,
    // even though it is disabled the same way while it is working.
    layersZipExporting: 'Zipping…',
    layersZipFailed: 'The ZIP per layer could not be downloaded.',
    // xTool role split (Task 7): engrave and cut as separate files, only
    // shown when xTool is the chosen machine profile.
    roleSplitZip: 'Engrave & cut separately (ZIP)',
    // M2: its own busy label -- otherwise this button says the same thing
    // as the other three export buttons while it is working.
    roleSplitZipExporting: 'Splitting…',
    roleSplitProFeature: 'Engrave & cut separately is a Pro feature.',
    // "Everything as ZIP" (Task 8): svg, dxf, png and pdf for the chosen
    // machine profile, plus fcm for Brother -- one file per sensible format.
    bundleZip: 'Everything as ZIP',
    // M2: its own busy label, for the same reason as the role split button.
    bundleZipExporting: 'Zipping…',
    bundleProFeature: 'Everything as ZIP is a Pro feature.',
    // The shop kit (Etsy round): cut files, four product previews and a
    // listing draft -- what a seller uploads to their shop.
    shopKit: 'Shop kit as ZIP',
    shopKitExporting: 'Zipping…',
    shopKitHint:
      'Cut files, four product previews (mug, T-shirt, folded card, glass), a draft for the listing text, plus a product CSV for Shopify and a filled-in Etsy form.',
    shopKitTodo:
      'Price, material and shipping are yours to fill in — they are left as gaps in square brackets. The Shopify product arrives as a draft, so nothing half-filled can go on sale.',
    shopKitProFeature: 'The shop kit is a Pro feature.',
    // The licence of the template this project was opened from -- here,
    // because the export is where a motif becomes a file somebody sells
    // (Etsy round).
    licenseTitle: 'Licence of the template',
    licenseFrom: 'This project comes from „{title}“ — licence: {license}.',
    licenseLabelPersonal: 'Personal',
    licenseLabelCommercial: 'Commercial',
    licenseLabelAttribution: 'With attribution',
    licensePersonal: 'Selling is not allowed — neither the file nor what you cut from it.',
    licenseCommercial: 'You may sell what you cut. Passing the file on or reselling it is not covered.',
    licenseAttribution: 'You may sell what you cut as long as you name {author}. Passing the file on is not covered.',
    licenseAuthorUnknown: 'the author',
    licenseProof: 'Licence proof as PDF',
    licenseOpenTemplate: 'Open the template',
    // Tiling (W6): panels for motifs larger than the mat.
    tilingProFeature: 'Tiling is a Pro feature.',
    exported: '{name} exported',
    failed: 'Export failed.',
    saveFailed: 'Saving before the export failed.',
    // Stands behind the error message when "weld the overlaps" was ticked:
    // the welding was for the export, and without an export nothing of it
    // stays in the document.
    weldReverted: 'The welding has been taken back.',
    invalidNumber: 'Please enter a number, 80.5 for example.',
    // The same range the export controller checks. Naming it here saves an
    // export that only fails with a 422 after the welding.
    widthRange: 'The width has to be between {min} and {max} mm.',
    cutReport: 'Cut report',
    // The mat view: one mat per visible layer/colour, in the order in which
    // the machine software calls the colours up. (`mat` on its own is the
    // material setting further up.)
    matsTitle: 'Mats — one per colour',
    matNumber: 'Mat {number}',
    history: {
      title: 'Exported recently',
      // Named honestly: no file is lying ready, a click builds it again.
      hint: 'No file cache — one click makes the file again, with the same settings.',
      loading: 'Loading…',
      loadFailed: 'The export history could not be loaded.',
      empty: 'No export yet.',
      layersZip: 'ZIP per layer',
      printCut: 'Print & Cut',
      roleSplit: 'Engrave & cut separately',
      bundle: 'Everything as ZIP',
      shopKit: 'Shop kit',
      // Its own part of the line when the export was mirrored -- repeating
      // the entry builds the mirrored file again.
      mirrored: 'mirrored',
      tiling: 'Tiling',
      restore: 'Export again',
    },
    errors: {
      pro_required: 'This format needs a Pro account.',
      empty_document: 'This project has no vectorised document yet.',
      writer_unavailable: 'This export format is unavailable right now. Please try again later.',
      invalid_param: 'Invalid export options.',
      invalid_document: 'This document could not be exported.',
      motif_too_large: 'The motif does not fit into the printable area of this page. Pick a larger page or a smaller width.',
      unsupported_raster: 'The project image is not a PNG, JPEG or WebP and cannot be printed.',
      export_timeout: 'The export took too long and was stopped. Try a smaller width or a simpler document.',
      lint_failed: 'The file that came out does not follow the rules of this machine and was not delivered. Please report the project to us.',
      nothing_to_split: 'This document has only engrave layers or only cut layers — there is nothing to split.',
    },
  },

  printCut: {
    title: 'Print & Cut',
    hint: 'A PDF to print with registration marks, and the cut line for the plotter in the same coordinates.',
    marks: 'Marks',
    page: 'Page',
    pageWidth: 'Page width (mm)',
    pageHeight: 'Page height (mm)',
    orientation: 'Orientation',
    portrait: 'Portrait',
    landscape: 'Landscape',
    bleed: 'Bleed {bleed}',
    includeDxf: 'Write the cut line as a DXF as well',
    preview: 'Preview: sheet {width} × {height} mm with {marks} marks',
    proFeature: 'Print & Cut is a Pro feature.',
    // Only shown while "mirror for iron-on" is ticked: Print & Cut refuses
    // the mirror on purpose -- the sheet is printed and cut, not pressed on,
    // and a mirrored print would simply be wrong.
    noMirror: 'Print & Cut is not mirrored: the sheet is printed and cut, not pressed on.',
    button: 'Print & Cut ZIP',
    // M2: its own busy label -- otherwise the Print & Cut button says the
    // same thing as the other three ZIP buttons while it is working.
    exporting: 'Preparing…',
  },

  // Tiling (W6, Pro): a motif larger than the mat or roll is split into
  // overlapping panels — signs, wall decals.
  tiling: {
    title: 'Tiling',
    hint: 'Splits the motif into overlapping panels, one file per panel. The cut edges at the tile borders stay open — the panels are glued with overlap, not cut.',
    // Why the buttons are disabled right now: there is nothing to split,
    // the plain export above does the job.
    fits: 'The document fits the chosen tile size — the plain export is enough.',
    // Deliberately not the words "Width (mm)" — the field at the top of the
    // panel already goes by that name, and two identically named fields are
    // indistinguishable for screen readers (and the E2E locators); same reason this says 'type', not 'format'.
    tileWidth: 'Width per tile (mm)',
    tileHeight: 'Height per tile (mm)',
    format: 'File type per strip',
    overlap: 'Overlap (mm)',
    marks: 'Alignment marks (crosses in the overlap zone)',
    // The name of the marks layer in every panel file (tiling.ts speaks no
    // language of its own, the name comes from here).
    markLayerName: 'Glue marks',
    // How many panels it will be, before anyone presses the button.
    grid: '{count} tiles — {rows} rows × {cols} columns, glued row by row.',
    invalid: 'Tile sides from {min} mm, the overlap has to be smaller than the tile.',
    tooMany: 'That would be more than {max} tiles. Pick larger tiles or less overlap.',
    preview: 'Preview: {count} tiles over the document',
    proFeature: 'Tiling is a Pro feature.',
    button: 'Tiling ZIP',
    exporting: 'Tiling…',
  },

  text: {
    addTitle: 'Add text',
    editTitle: 'Edit text',
    text: 'Text',
    placeholder: 'Happy Easter',
    font: 'Font',
    height: 'Height (mm)',
    alignment: 'Alignment',
    left: 'Left',
    center: 'Centred',
    right: 'Right',
    groupSans: 'Sans & serif',
    groupScript: 'Script',
    groupHandwriting: 'Handwriting',
    groupDisplay: 'Display',
    groupSingleLine: 'Writing line (single stroke)',
    singleLineHint: 'Single stroke for a pen plotter or for engraving: every letter is a line rather than an area. No welding, and no flow along a path or a circle.',
    invalidHeight: 'Please enter a height between {min} and {max} mm, 20 or 12.5 for example.',
    editHint: 'Setting the text again replaces every shape in this layer — everything done to it by hand (nodes, eraser, welding) goes with it. Position and size stay.',
    addHint: 'The letters become outlines in a new layer — ready to cut. The layer remembers the text, so you can set it again later. The height is the em; capitals come out at about 70 % of it.',
    nothingToSet: 'Nothing to set — the text has no letters.',
    fontFailed: 'The font could not be loaded.',
    fontLoadFailed: 'The font could not be loaded — please open the editor once while online.',
    setting: 'Setting…',
    update: 'Update the text',
    add: 'Add text',
    cancel: 'Cancel',
    layout: 'Flow',
    layoutBlock: 'As a block',
    layoutPath: 'Along a path',
    layoutCircle: 'In a circle',
    pathHint: 'Select exactly one path before opening this — it becomes the guide path.',
    startOffset: 'Start offset',
    startUnit: 'Unit of the start offset',
    unitMm: 'mm',
    unitPercent: '%',
    spacing: 'Letter spacing (mm)',
    side: 'Side',
    sideTop: 'Top',
    sideBottom: 'Bottom',
    radius: 'Radius (mm)',
    invalidRadius: 'Please enter a radius between {min} and {max} mm.',
    invalidNumber: 'Please enter a number, 0 or 2.5 for example.',
    deleteGuide: 'Delete the guide path',
    weld: 'Weld the letters',
    weldTitle: 'Unites the letters into one silhouette — holes stay. For script fonts this is the default.',
    pathHintCircle: 'The guide circle is only a parameter — only the letters go into the document.',
    editPathHint: 'While editing, the letters return to the stored guide path — even if the layer has been moved since.',
    skipped: { one: '1 letter no longer fitted onto the guide path.', other: '{count} letters no longer fitted onto the guide path.' },
    kerning: 'Letter spacing',
    kerningHint: 'Tap a letter and adjust the gap before it — negative closes it, and it moves with everything after it. Raise/lower shifts only that letter (positive is down). Kept whenever the text is set again.',
    kerningDx: 'Gap before (mm)',
    kerningDy: 'Raise/lower (mm)',
    kerningReset: 'Reset',
    kerningResetAll: 'Remove all spacing tweaks',
    kerningSpace: 'Space',
    manualEditsWarning: 'This layer has been changed by hand since it was last set (nodes, eraser, welding or similar). Setting the text again discards those changes.',
  },

  mobile: {
    tabs: {
      select: 'Select',
      edit: 'Edit',
      color: 'Colour',
      paint: 'Paint',
      crop: 'Crop',
      layers: 'Layers',
      more: 'More',
    },
    sheets: {
      layers: 'Layers',
      colors: 'Colour',
      path: 'Path',
      paint: 'Paint',
      crop: 'Crop',
      more: 'More',
      export: 'Export',
      arrange: 'Arrange',
    },
    tool: 'Tool',
    recolor: 'Recolour',
    smartFill: 'Smart fill',
    pick: 'Pick up',
    brush: 'Brush',
    eraser: 'Eraser',
    projectSection: 'Project',
    textSection: 'Text',
    insertSection: 'Insert',
    cutMeasureSection: 'Cut & measure',
    flipSection: 'Flip',
    flipHorizontal: 'Horizontal',
    flipVertical: 'Vertical',
    addText: 'Add text',
    community: 'Community',
    viewTemplate: 'View the template',
    renameTitle: 'Rename the project',
    renameLabel: 'Project name',
    rename: 'Rename',
    export: 'Export',
    actionBar: {
      nodeActions: 'Actions for path nodes',
      selectionActions: 'Actions for the selection',
      deleteNodes: 'Delete the nodes',
      delete: 'Delete',
      doneEditing: 'Finished editing the nodes',
      done: 'Done',
      color: 'Colour',
      additiveSelect: 'Multiple selection',
      additiveSelectOn: 'Multiple selection off',
      duplicate: 'Duplicate',
      forward: 'One step forward',
      back: 'One step back',
      toFront: 'To the front',
      toBack: 'To the back',
      editNodes: 'Edit the nodes',
      unite: 'Unite',
      subtract: 'Subtract',
      contour: 'Outline…',
      arrange: 'Arrange',
      flipHorizontal: 'Flip horizontal',
      flipVertical: 'Flip vertical',
      flipHorizontalHint: 'Flip for iron-on vinyl',
      penActions: 'Actions for the pen',
      penUndoPoint: 'Take the last point back',
      penDone: 'Finish drawing the path',
      group: 'Group the selection',
      ungroup: 'Ungroup',
    },
  },

  sheet: {
    resize: 'Resize the “{title}” area',
    close: 'Close {title}',
  },

  toast: {
    dismiss: 'Dismiss the message',
  },

  dialog: {
    close: 'Close',
    confirm: 'Confirm',
    cancel: 'Cancel',
    save: 'Save',
  },

  // Insert: the menu on the toolbar and the dialog that puts another project
  // of your own into the open one.
  insert: {
    menu: 'Insert',
    project: 'Project…',
    box: 'Box…',
    qr: 'QR code…',
    testStrip: 'Test strip…',
    title: 'Insert a project',
    search: 'Search your projects…',
    noProjects: 'No other project found.',
    prevPage: 'Back',
    nextPage: 'Next',
    pageOf: 'Page {page} of {pages}',
    position: 'Position',
    positionCenter: 'Centre',
    positionPointer: 'At the last pointer position',
    size: 'Size',
    sizeOriginal: 'Original',
    sizeWidth: 'To a width',
    widthMm: 'Width (mm)',
    grow: 'Grow the canvas if needed',
    submit: 'Insert',
    cancel: 'Cancel',
    loading: 'Loading…',
    loadFailed: 'The project could not be loaded.',
    nothing: 'This project has no paths.',
    done: { one: '1 layer inserted.', other: '{count} layers inserted.' },
    undoLabel: 'Insert the project “{name}”',
    mobileImport: 'Insert a project…',
  },

  // A project without a source image: created empty or opened from a
  // template. What is different about it is what these four sentences say --
  // on the canvas, on the locked tool and in the bitmap panel.
  blank: {
    noImageTool: 'No source image',
    noImagePanel: 'This project has no source image — the smart fill, cropping and the photo mode all need one.',
    emptyTitle: 'Empty canvas ({width} × {height} mm)',
    emptyBody: 'Add text (T) or bring something in through “Insert”.',
  },

  // "Insert a file": images and PDF scans run through the vectorisation and
  // cost a daily slot, an SVG is read by the browser itself and costs nothing.
  // The box generator (Insert → Box…): an open box to size, with the cut and
  // the fold layer kept apart (geometry/boxTemplate.ts).
  box: {
    title: 'Box',
    length: 'Length (mm)',
    width: 'Width (mm)',
    height: 'Wall height (mm)',
    flap: 'Glue flap (mm)',
    create: 'Create',
    cancel: 'Cancel',
    cutLayer: 'Box — cut',
    foldLayer: 'Box — fold',
    invalid: 'Please enter measurements between {min} and {max} mm.',
    done: 'Box created — the cut and the fold layer are on top.',
    hint: 'A base with four walls and glue flaps. The fold lines sit in a line layer of their own; the canvas grows if the unfolded box does not fit on it.',
  },

  // "Insert → QR code…": text or a URL goes to `POST /api/v1/qr_paths`
  // (rqrcode on the server), the module grid comes back and becomes cut paths
  // in the browser (geometry/qrCode.ts) — no SVG parsing, no image
  // vectorisation, no daily quota.
  qr: {
    title: 'QR code',
    text: 'Text or URL',
    textPlaceholder: 'https://… or a short text',
    size: 'Size (mm)',
    create: 'Insert',
    cancel: 'Cancel',
    layer: 'QR',
    invalidText: 'Please enter a text between 1 and 500 characters.',
    invalidSize: 'Please enter a size between {min} and {max} mm.',
    requestFailed: 'The QR code could not be created.',
    done: 'QR code inserted — it sits on top, in the layer “QR”.',
    hint: 'The light border around the code (the quiet zone) belongs to it — without it no camera reads it reliably. The layer holds only the dark modules, no white backing: the material and the surface underneath have to stay light themselves, otherwise the quiet zone disappears into the contrast and the code no longer reads.',
    errorCorrection: 'Error correction',
    levelL: 'L — 7%, smallest code',
    levelM: 'M — 15%',
    levelQ: 'Q — 25%',
    levelH: 'H — 30%, recommended',
    logo: 'Free area in the middle (% of the area, at most {max})',
    logoHint:
      'The free area stays empty — it is a hole in the pattern, not an image. You place your logo there yourself: as a layer already in the document, or through “Insert → File…”.',
    logoArea: 'The hole will be roughly {size} mm square — a little more or less, depending on the text length.',
    logoTooLarge:
      'This code cannot carry that much free area: the error correction would have to make up for more than it can. At most {max}% works for this text.',
    logoImpossible:
      'No free area is possible for this text: the code needs a version that carries an alignment pattern right in the middle. A shorter text (or a short redirect URL) leaves room for a logo.',
    useMaxLogo: 'Set to {max}%',
    invalidPercent: 'Please enter a free area between 0 and {max}% of the area.',
    style: 'Styling',
    roundModules: 'Round the modules',
    roundMarkers: 'Round the position markers',
    levelRaised: 'Error correction raised to {level} — without it the free area would not be defensible.',
    doneWithLogo: 'QR code inserted — the free area in the middle is {size} mm and waits for your logo.',
    tooSmall:
      'At this size a module is only {mm} mm wide. Below {min} mm no camera reads it reliably — make it larger or use less text.',
    unreadable:
      'The code that came out does not read back: {count} modules disagree with the pattern. Nothing inserted — an unreadable QR code looks just like a readable one.',
  },

  // "Insert → Test strip…": N same-sized fields in a row, each with a
  // running number above it (geometry/testStrip.ts, the numbers a
  // Hershey-Simplex outline through singleLineFont.ts). Meant for cutting
  // each field with a different setting — which number goes with which
  // setting is for the user to remember; the document never stores a single
  // cutting value.
  testStrip: {
    title: 'Test strip',
    count: 'Number of fields',
    fieldSize: 'Field size (mm)',
    gap: 'Gap (mm)',
    shape: 'Shape',
    shapeSquare: 'Square',
    shapeCircle: 'Circle',
    create: 'Create',
    cancel: 'Cancel',
    layerName: 'Test strip',
    invalidCount: 'Please enter a count between {min} and {max} fields.',
    invalidField: 'Please enter a field size between {min} and {max} mm.',
    invalidGap: 'Please enter a gap between {min} and {max} mm.',
    done: 'Test strip created — which number matches which setting is yours to remember.',
    hint: 'Every field carries a number. Cut the row with a different setting per field and see which one comes out clean — the number-to-setting match lives nowhere in the document, only with you at the machine.',
  },

  insertFile: {
    menu: 'File…',
    mobile: 'Insert a file…',
    title: 'Insert a file',
    drop: 'Drag a PNG, JPEG, WebP, HEIC, AVIF, PDF or SVG here — or',
    choose: 'Choose a file',
    // Two counts in one sentence, each inflected on its own: "1 path in 1 layer".
    svgSummary: '{paths} in {layers}',
    svgPaths: { one: '1 path', other: '{count} paths' },
    svgLayers: { one: '1 layer', other: '{count} layers' },
    quotaHint: 'Costs one vectorisation of your daily quota.',
    quotaHintPro: 'Costs one vectorisation.',
    // PDF page selection (Task 6, round four): only shown when the chosen
    // file is a PDF.
    pdfPage: 'PDF page',
    position: 'Position',
    positionCenter: 'In the centre',
    positionPointer: 'At the last pointer position',
    size: 'Size',
    sizeOriginal: 'Original size',
    sizeWidth: 'To a width',
    widthMm: 'Width (mm)',
    invalidWidth: 'Please enter a width in millimetres, 80.5 for example.',
    grow: 'Let the canvas grow along',
    submit: 'Insert',
    cancel: 'Cancel',
    uploading: 'Uploading…',
    converting: 'Vectorising ({status})…',
    // The stages of a conversion, named the way the dashboard names them too
    // (components/ProjectsDashboard.tsx).
    statuses: {
      pending: 'waiting',
      preprocessing: 'preparing',
      tracing: 'running',
      postprocessing: 'finishing',
    },
    done: { one: '1 layer inserted.', other: '{count} layers inserted.' },
    // After an image that failed: the slot is gone, and the user should know
    // that before trying again.
    retryCosts: 'Another attempt costs one more vectorisation of your daily quota.',
    undoSvg: 'Insert the SVG “{name}”',
    undoImage: 'Insert the image “{name}”',
    // "Create a motif" in the same dialog: the suggestion comes from the
    // external image service (docs/GENERATION.md) and afterwards goes through
    // the same image branch as a chosen file.
    generateLegend: 'Or describe a motif',
    generatePlaceholder: 'e.g. An anchor with a rope',
    generateStyle: 'Style',
    generateRun: 'Create a suggestion',
    generateBusy: 'Creating the suggestion…',
    generateHint: 'The description goes to an external image service; every suggestion costs a slot of the daily AI limit.',
    generateLimit: "You have reached this month's AI image allowance. You can buy 20 more requests for €4.99 from your account page.",
    generateUnavailable: 'The image service is unavailable right now. Try again later.',
    generateFailed: 'The suggestion could not be created. Please try again.',
    errors: {
      'too-many-nodes': 'The SVG has more than 20,000 nodes.',
      empty: 'The SVG holds no paths.',
      invalid: 'The file is not a valid SVG.',
      unsupportedType: 'Only PNG, JPEG, WebP, HEIC, AVIF, PDF, AI, EPS or SVG.',
      tooLarge: 'The file is larger than 20 MB.',
      limitReached: 'Daily limit reached ({used} / {limit}).',
      conversionFailed: 'The vectorisation failed.',
      emptyResult: 'The vectorisation produced no layers. Another image or different settings usually help.',
      uploadFailed: 'The file could not be uploaded.',
      // PDF page selection (Task 6): no slot spent, so no `retry` hint the
      // way a failed vectorisation gets one.
      pdfPageOutOfRange: {
        one: 'That page does not exist — the PDF has only 1 page.',
        other: 'That page does not exist — the PDF has {count} pages.',
      },
      // Round four, task 5 (I5): translations of the frozen German
      // `UserFacingError` sentences the engines raise (skeleton.rb,
      // postprocess.rb) — the server matches its stored message against
      // `Vectorization::UserFacingError::CODES` and hands back one of these
      // codes, or none for a message this table does not cover, in which
      // case the German server text is shown instead (docs/BRAND.md, "V1
      // boundary": a conversion that failed before this table existed keeps
      // reading correctly, just untranslated).
      too_fine:
        'The lines are too fine for centreline detection — nothing is left of them once the image is prepared. A drawing with bolder strokes usually helps, or vectorising it as a filled shape (vtracer) instead.',
      too_many_points:
        'This image produces too many separate lines — as a centreline it could no longer be edited. A clear line drawing works best; for filled areas, vtracer is the right engine.',
      unfit_document:
        'This image does not produce a cut file that can still be edited — the outlines are too fine. Try fewer colours or a higher small-shape threshold.',
      // Task 7 (legacy-cleanup round): translation of `Normalize::PDF_ERROR`.
      pdf_unreadable:
        'The PDF file could not be read. Encrypted and empty PDFs cannot be vectorised.',
    },
  },

  // The SVG doctor (docs/superpowers/specs/2026-08-30-svg-doctor-design.md).
  //
  // A finding says two things: *what* is in the file (the line, inflected on
  // the number) and *what becomes of it* (the severity as a word in front).
  // The severity is text and not a colour on purpose -- the list stands in two
  // dialogs, is read aloud, and has to be readable without colour too.
  svgDoctor: {
    severity: {
      blocked: 'Will be lost',
      dropped: 'Will be removed',
      repairable: 'Repairable',
      note: 'Note',
    },
    findings: {
      'embedded-image': {
        one: '1 embedded image — a plotter cuts nothing out of it.',
        other: '{count} embedded images — a plotter cuts nothing out of them.',
      },
      'external-image': {
        one: '1 image from another server — it cannot be fetched here.',
        other: '{count} images from other servers — they cannot be fetched here.',
      },
      text: {
        one: '1 text element — Schnittklar does not have the font of the file. Set the text again in the editor.',
        other: '{count} text elements — Schnittklar does not have the font of the file. Set the text again in the editor.',
      },
      'clip-path': {
        one: '1 clipping path — whatever it hides would otherwise quietly reappear.',
        other: '{count} clipping paths — whatever they hide would otherwise quietly reappear.',
      },
      mask: { one: '1 mask — a blade knows no mask.', other: '{count} masks — a blade knows no mask.' },
      filter: {
        one: '1 filter — blurs and shadows have no cutting edge.',
        other: '{count} filters — blurs and shadows have no cutting edge.',
      },
      'foreign-object': {
        one: '1 embedded HTML element.',
        other: '{count} embedded HTML elements.',
      },
      gradient: {
        one: '1 gradient — it becomes one layer in black.',
        other: '{count} gradients — they become one layer in black.',
      },
      'style-block': {
        one: '1 <style> block — colours from CSS classes do not come along.',
        other: '{count} <style> blocks — colours from CSS classes do not come along.',
      },
      'use-element': {
        one: '1 reference to another shape (<use>) — it will be resolved.',
        other: '{count} references to other shapes (<use>) — they will be resolved.',
      },
      'no-units': {
        one: 'The file does not say how large it is — 96 dpi is assumed.',
        other: 'The file does not say how large it is — 96 dpi is assumed.',
      },
      duplicates: {
        one: '1 duplicate path — it would be cut twice.',
        other: '{count} duplicate paths — they would be cut twice.',
      },
      'small-parts': {
        one: '1 part under 1 mm — it stays behind on the mat.',
        other: '{count} parts under 1 mm — they stay behind on the mat.',
      },
      overlaps: {
        one: '1 layer with overlapping paths — the edge is driven twice.',
        other: '{count} layers with overlapping paths — the edges are driven twice.',
      },
      'node-budget': {
        one: '1 node more than a plotter drives smoothly.',
        other: '{count} nodes more than a plotter drives smoothly.',
      },
      'stroke-only': {
        one: '1 layer is made of outlines only — the line is cut, not the area.',
        other: '{count} layers are made of outlines only — the lines are cut, not the areas.',
      },
      'loose-parts': {
        one: '1 loose inner part — it would fall out when a stencil is cut.',
        other: '{count} loose inner parts — they would fall out when a stencil is cut.',
      },
      // The two findings of the real vector import (PDF/AI/EPS).
      'outlined-text': {
        one: '1 character arrived as an outline — cuttable, but no longer editable as text.',
        other: '{count} characters arrived as outlines — cuttable, but no longer editable as text.',
      },
      transparency: {
        one: '1 partly transparent shape — it is cut in full; a blade knows no "half there".',
        other: '{count} partly transparent shapes — they are cut in full; a blade knows no "half there".',
      },
    },
    // The switches of the findings dialog. Their number comes from the repair
    // and not from the finding: it says what this switch really changes.
    repairs: {
      duplicates: {
        one: 'Remove 1 duplicate path',
        other: 'Remove {count} duplicate paths',
      },
      'small-parts': {
        one: 'Remove 1 part under 1 mm',
        other: 'Remove {count} parts under 1 mm',
      },
      overlaps: {
        one: 'Weld the overlaps in 1 layer',
        other: 'Weld the overlaps in {count} layers',
      },
      'node-budget': {
        one: 'Save 1 node (changes the shape slightly)',
        other: 'Save {count} nodes (changes the shape slightly)',
      },
    },
    dialog: {
      title: 'Repair the SVG',
      reading: 'Reading the file…',
      // Two sentences instead of one: `tn` inflects one number, and the dialog
      // has two. `svgDoctor.summaryMessage` puts them together.
      summaryPaths: {
        one: '1 cuttable path',
        other: '{count} cuttable paths',
      },
      summaryLayers: {
        one: 'in 1 layer.',
        other: 'in {count} layers.',
      },
      repairs: 'Repair while creating',
      width: 'Target width (mm)',
      widthHint: '{min} to {max} mm — the height follows the aspect ratio.',
      rescueHint:
        'This file holds no cut, it holds an image. Schnittklar can vectorise it — that costs one of your daily slots.',
      rescue: 'Pull the image out and vectorise it',
      create: 'Create the project',
      creating: 'Creating…',
      cancel: 'Cancel',
    },
    errors: {
      empty: 'There is nothing in this file that a plotter could cut.',
      'too-many-nodes': 'This file has too many nodes for the editor. Simplify it in your drawing program.',
      invalid: 'This file is not a readable SVG file.',
      width: 'The target width has to be between {min} and {max} mm.',
      create: 'The project could not be created. Please try again.',
      rateLimited: 'Too many SVG files in a short time — try again later.',
      read: 'The file could not be read. Choose it again.',
      // The height follows the aspect ratio: at full width a very tall motif
      // bursts the page the server accepts. A second attempt changes nothing
      // about that — a smaller target width does.
      tooTall: 'At {width} mm wide the motif would be {height} mm tall — more than {max} mm. Pick a smaller target width.',
      invalidDocument: 'No project that Schnittklar accepts can be built from this file.',
      // The honest refusal at the limits of the document schema: with the
      // number and the limit, so it says what to do about it. A second
      // attempt changes nothing.
      'too-many-layers':
        'This file has {count} colours and therefore {count} layers — Schnittklar holds {limit}. ' +
        'Merge some colours in your drawing program.',
      'too-many-paths':
        'One layer of this file has {count} paths — Schnittklar holds {limit} per layer. ' +
        'Simplify the file in your drawing program.',
      'path-too-long':
        'A single path in this file is too tangled for the editor ' +
        '({count} characters, the limit is {limit}). Simplify it in your drawing program.',
    },
  },

  // The real vector import: what happens when someone picks a PDF, AI or EPS
  // file that has paths in it (`lib/vectorSource.ts`).
  vectorSource: {
    title: 'This file contains vectors',
    body:
      'This file has finished paths in it. Schnittklar can take them over directly — ' +
      'lossless, in the original colours and without spending a slot of your daily allowance. ' +
      'Or the page is rasterised and vectorised again, as before.',
    pageHint: 'Page {page} of {pages}.',
    direct: 'Take the vectors over',
    retrace: 'Vectorise again',
    cancel: 'Cancel',
    unavailable: 'This file contains vectors, but this server cannot take them over directly.',
    failed: 'The file could not be checked for vectors. It will be vectorised as before.',
  },

  ai: {
    title: 'AI',
    // The same wording as on the pricing page (app/models/billing.rb), so the
    // panel promises what is sold there and nothing else.
    proFeature: 'Remove the background automatically (runs in the browser)',
    proHint: '{feature} — a Pro feature.',
    phaseRuntime: 'Loading the AI runtime',
    phaseDownload: 'Loading the model',
    phaseLoad: 'Preparing the model',
    phaseInfer: 'Computing the image',
    // The progress of a download: with a percentage when the server named its
    // size, otherwise only the megabytes loaded. The runtime is 28 MB —
    // without a number next to it a slow line looks like a hang.
    phaseBytes: '{phase} — {done} of {total} ({percent} %)',
    phaseBytesUnknown: '{phase} — {done} loaded',
    errorUnsupported: 'This browser cannot run the models (no WebAssembly with SIMD).',
    errorDownload: 'The model could not be loaded. Please check your connection.',
    errorInference: 'The computation failed.',
    errorCancelled: 'Cancelled.',
    errorStalled: 'No progress for 60 seconds — cancelled.',
    removeBackground: 'Remove the background',
    upscale: 'Upscale ×2',
    checkingBrowser: 'Checking the browser…',
    backendWebgpu: 'Runs in the browser over WebGPU.',
    backendWasm: 'Runs in the browser over WebAssembly.',
    noImage: 'This project has no image.',
    replaceFailed: 'The working image could not be replaced.',
    uploadFailed: 'The new working image could not be uploaded. It stays in the editor and will be tried again.',
    replaced: 'Working image replaced.',
    retraceTitle: 'Re-vectorise',
    retraceConfirm: 'Re-vectorise',
    replacedRetracing: 'Working image replaced, the image is being vectorised again…',
    // When the new working image never reached the server, there must be no
    // re-vectorising — the server would have drawn the old image. The sentence
    // says both: what holds now and how to catch up.
    retraceSkipped:
      'The working image is replaced, but it did not arrive at the server — that is why nothing was vectorised again. The vectors still belong to the old image. As soon as it says “Saved” up there, start it in the “Re-vectorise” panel.',
    limitReached: 'Daily limit reached — the working image is replaced, the vectorisation has not started.',
    retraceFailed: 'The vectorisation could not be started. The working image is replaced.',
    tooLargeTitle: 'The result would be larger than {max} px.',
    tooLargeHint: 'Upscaling is off: the result would be larger than {max} px.',
    starting: 'Starting…',
    phaseRunning: '{phase} …',
    cancel: 'Cancel',
    retry: 'Try again',
    before: 'Before',
    after: 'After',
    applyReplace: 'Apply — replace the working image',
    applyRetrace: '… and vectorise again',
    discard: 'Discard',
    // Editing by instruction: unlike the two functions above it, this does not
    // run in the browser but at the image service (docs/GENERATION.md).
    edit: 'AI editing',
    editLabel: 'Edit with an instruction',
    editPlaceholder: 'e.g. Remove the lettering at the bottom left',
    editRun: 'Edit',
    editWorking: 'Editing the image — this can take up to a minute…',
    editHint: 'The image is sent to an external image service for this.',
    editFailed: 'The edit failed. Try again.',
    editUnavailable: 'The image service is unavailable right now. Try again later.',
    editLimit: "You have reached this month's AI image allowance. You can buy 20 more requests for €4.99 from your account page.",
    // One-click suggestions above the text field: they fill the prompt
    // (replacing it, not appending) and focus the field, but send nothing —
    // sending stays a deliberate act, because it costs quota.
    presetComplete: 'Complete the motif',
    presetRepair: 'Repair what is damaged',
    presetCompletePrompt:
      'Complete the cropped motif seamlessly in the same style, flat colours, clear outlines, white background.',
    presetRepairPrompt:
      'Repair the damaged spots seamlessly in the same style, flat colours, clear outlines, white background.',
  },

  publish: {
    title: 'Publish',
    titleNew: 'Publish as a template',
    titleUpdate: 'Update the template',
    serverFallback: 'That did not work. Try again in a moment.',
    needsAlias: 'A template always carries an author handle — without one the link on the template page leads nowhere. Set an alias in your account first.',
    needsPublicProfile: 'The template links to your profile, and that is private right now — the link would go nowhere for everybody else. Make your profile public first.',
    cancel: 'Cancel',
    toAccount: 'Go to your account',
    updated: 'Template updated.',
    published: 'Template published.',
    view: 'View',
    fieldTitle: 'Title',
    fieldDescription: 'Description (optional)',
    languageLegend: 'Language of the texts',
    languageGerman: 'Deutsch',
    languageEnglish: 'English',
    englishHint: 'Optional: this is how the template appears to visitors using Schnittklar in English. The English tags follow the order of the German ones.',
    germanRequiredHint: 'The German title is required – the catalogue searches and filters in German.',
    translateFromGerman: 'Translate from German',
    translateFromEnglish: 'Translate from English',
    translating: 'Translating …',
    translated: 'Translated – please read it and adjust where needed.',
    translateProHint: 'With Pro, the other language can be translated in one click.',
    translateOverwrite: 'The fields of this language are already filled in. Replace them with the translation?',
    translatePro: 'Translating is a Pro feature.',
    translateLimit: 'Too many translations this hour. Please try again later.',
    translateUnavailable: 'Translating is not available right now. Please try again later.',
    translateFailed: 'The translation did not work.',
    fieldTags: 'Tags, separated by commas',
    tagsPlaceholder: 'easter, kids, flex',
    licenseLegend: 'What may others do with it?',
    fieldProfile: 'Machine profile',
    includeSourceImage: 'Publish the original image as well',
    rightsPrefix: 'I hold the rights to this motif and accept the',
    rightsLink: 'template terms',
    sending: 'One moment…',
    submit: 'Publish',
    licensePrivate: 'Private',
    licensePrivateHint: 'Others may cut it and give it away, but sell nothing made with it.',
    licenseCommercial: 'Commercial',
    licenseCommercialHint: 'Others may sell what they cut with it — not the file itself.',
    licenseAttribution: 'With attribution',
    licenseAttributionHint: 'Like commercial, but your name has to be named with it.',
    profileAny: 'Any machine',
    profileCricut: 'Cricut',
    profileXtool: 'xTool',
    // Difficulty, time estimate and materials list (4.5/4.6, round 5) — all
    // three optional, hence the "not specified" placeholders.
    fieldDifficulty: 'Difficulty (optional)',
    difficultyPlaceholder: 'Not specified',
    difficultyEasy: 'Easy',
    difficultyMedium: 'Medium',
    difficultyChallenging: 'Challenging',
    fieldTimeMinutes: 'Time needed in minutes (optional)',
    fieldMaterials: 'Materials, separated by commas (optional)',
    // Prefilled from the layer colours (see `materialSuggestions`) — the
    // placeholder still shows an example in case the author deletes the
    // suggestions.
    materialsPlaceholder: 'Blue vinyl, transfer tape',
    validation: {
      title: 'Give your template a title.',
      germanTitle: 'The German title is missing – enter it under "Deutsch" or have it translated.',
      maxLength: '{max} characters at most.',
      tooManyTags: '{max} tags at most — there are {count}.',
      tagLength: 'Every tag may be {max} characters long at most.',
      timeMinutes: 'Between {min} and {max} minutes.',
      tooManyMaterials: '{max} materials at most — there are {count}.',
      materialLength: 'Every material may be {max} characters long at most.',
      rights: 'Without this confirmation it will not go.',
    },
    // Where a derivative comes from (docs/COMMUNITY.md, "Remix"): the note
    // above the first field, and the dead end when the parent template allows
    // no derivatives.
    remixNotice: 'Will be published as a derivative of “{title}” by {author} — where it comes from is stated on the template page.',
    // When the author of the parent template has no alias: `null` must not
    // become "@null".
    remixAuthorFallback: 'the author',
    remixBlocked: 'This template is released for personal use only — you cannot publish a derivative of it.',
    // The duplicate hint (Task 10): never a barrier, only a look into the
    // catalogue before sending — the titles behind it are links.
    duplicateHint: 'Looks similar to:',
    // What the match means — the sentence that turns the information into a
    // decision. Reassurance first (no accusation, no barrier), then the route
    // that already exists: publishing as a remix names the source, a second
    // motif of your own beside it does not.
    duplicateAdvice:
      'This is not an accusation and not a barrier — two people may draw the same Easter bunny. ' +
      'If you opened one of these as a template and worked on from there, cancel here, open it with ' +
      '“Open in my projects” and publish the result from there: as a remix it names its source and ' +
      'credits the original author.',
    // The same reassurance without the remix advice: these motifs are
    // released for personal use only, and the server refuses a derivative
    // (`remix_not_allowed`).
    duplicateAdviceNoRemix:
      'This is not an accusation and not a barrier — two people may draw the same Easter bunny. ' +
      'A derivative is not the way here, though: these motifs are released for personal use only. ' +
      'So publish only what came out of your own drawing.',
    errors: {
      rights_not_confirmed: 'Without confirming the rights it will not go.',
      alias_required: 'Set an alias in your account first.',
      profile_not_public: 'Make your profile public first — the template links to it.',
      email_unconfirmed: 'Confirm your email address first — the link for it is in your inbox.',
      empty_document: 'This project has no document yet that could be published.',
      invalid: 'The form is not complete yet.',
      not_found: 'This project does not exist any more.',
      parent_hidden: 'The template this project comes from is not in the catalogue right now — wait until it is back.',
      paid_source: 'This project comes from a template bundle you bought. You may cut and sell what you make from it, but not publish it in the catalogue yourself.',
    },
  },

  // The share dialog (S-8, docs/superpowers/specs/2026-08-29-share-link-design.md).
  // A link is not a publication: no catalogue, no licence, no moderation --
  // which is why this says "share" and nowhere "publish".
  share: {
    title: 'Share the result',
    open: 'Share',
    // On the button, as long as there is nothing to share (S-10).
    emptyHint: 'Vectorise first, then share: this project has no layers yet.',
    active: 'Link active',
    // What the recipient sees -- and what they do not. The sentence stands
    // right at the switch, because it is the decision made there.
    intro: 'Whoever has the link sees the project name, the result and the layers, and can download the SVG. Your original image stays here — it never goes along.',
    off: 'The link is off. Turn it on and you get an address to send.',
    urlLabel: 'Link to send',
    copy: 'Copy',
    copied: 'Copied',
    copyFailed: 'Copying did not work here — the link is selected, copy it with Ctrl/⌘+C.',
    views: { one: '1 view', other: '{count} views' },
    rotate: 'Create a new link',
    rotateTitle: 'Create a new link?',
    rotateBody: 'The old link becomes invalid immediately — whoever already has it will only see an error page afterwards. You have to send the new one out yourself.',
    rotateConfirm: 'Create a new link',
    done: 'Close',
    serverFallback: 'That did not work. Try again in a moment.',
    errors: {
      empty_document: 'This project has no result yet that could be shared.',
      not_found: 'This project does not exist any more.',
      forbidden: 'Only the owner of a project can share it.',
      too_many_requests: 'Too many changes in a short time — try again in a moment.',
    },
  },

  // The version history (docs/EDITOR.md, "Versionen"): the panel, the preview
  // and the questions back. The vector document only -- the working image has
  // no history, and the panel would rather say so than keep quiet about it.
  history: {
    title: 'History',
    dialogTitle: 'Versions',
    intro: 'Earlier states of the document. The working image is not versioned.',
    loading: 'Loading the history…',
    loadFailed: 'The history could not be loaded.',
    empty: 'No versions yet — the first one appears at the next save.',
    save: 'Keep a version…',
    saveTitle: 'Keep a version',
    saveLabel: 'Label (optional)',
    saveConfirm: 'Keep',
    saved: 'Version kept.',
    saveFailed: 'The version could not be kept.',
    saveEmpty: 'An empty sheet has no state worth keeping.',
    preview: 'Preview of {title}',
    compareCurrent: 'Current',
    compareVersion: 'This version',
    restore: 'Restore',
    restoreTitle: 'Restore this state?',
    restoreBody: 'The document will be set to “{title}” ({time}). The current state is kept as a version first; undo starts from scratch afterwards.',
    restoreUnsavedBody: 'Unsaved changes will be lost: they could not be saved just now. The document will be set to “{title}” ({time}); the last saved state is kept as a version first.',
    restoreConfirm: 'Restore',
    restored: 'State restored — the previous one is in the history.',
    restoreFailed: 'The restore did not work.',
    close: 'Close',
    // One line per entry: "3 min ago · Automatic · 48 KB".
    meta: '{time} · {origin} · {size}',
    origin: {
      autosave: 'Automatic',
      manual: 'Kept by hand',
      before_restore: 'Before restoring',
      retrace: 'Before vectorising',
      import: 'Before inserting',
    },
    time: {
      justNow: 'just now',
      minutes: { one: '1 min ago', other: '{count} min ago' },
      hours: { one: '1 h ago', other: '{count} h ago' },
      days: { one: '1 day ago', other: '{count} days ago' },
    },
  },

  cutReport: {
    title: 'Did the cut work out?',
    serverFallback: 'That did not work. Try again in a moment.',
    devicesFailed: 'The list of machines could not be loaded. Reload the page.',
    thanks: 'Thank you — your cut report helps the next person.',
    view: 'View',
    introTemplate: 'Your report on “{title}” — afterwards it stands with the template and with your machine.',
    intro: 'Tell the others what worked for you. Many reports make the machine pages.',
    existing: { one: 'You have already written 1 report on this project.', other: 'You have already written {count} reports on this project.' },
    device: 'Machine',
    pleaseChoose: 'Please choose',
    material: 'Material',
    cutterSettings: 'Settings on the cutter',
    blade: 'Blade',
    pressure: 'Pressure',
    preset: 'Material preset',
    laserSettings: 'Settings on the laser',
    power: 'Power (%)',
    speed: 'Speed (mm/s)',
    passes: 'Passes',
    outcomeLegend: 'So, did it work?',
    exportedWith: 'Exported with what?',
    notes: 'Notes (optional)',
    photo: 'Photo of the result (optional)',
    photoHint: 'The photo is shown publicly with the machine and the template; we strip the metadata (location, camera).',
    cancel: 'Cancel',
    sending: 'One moment…',
    submit: 'Save the report',
    outcomeGood: 'Clean',
    outcomeGoodHint: 'Through on the first try.',
    outcomeAdjusted: 'Adjusted',
    outcomeAdjustedHint: 'Worked after a few changes.',
    outcomeFailed: 'Bad cut',
    outcomeFailedHint: 'Did not cut.',
    profileCricut: 'Cricut',
    profileXtool: 'xTool',
    profileOther: 'Another program',
    myDevices: 'My machines',
    validation: {
      integerRange: '{label}: a whole number between {min} and {max}.',
      device: 'Tell us which machine you cut with.',
      material: 'Without a material the report helps nobody.',
      maxLength: '{max} characters at most.',
      outcome: 'Did the cut work out?',
      power: 'Power: a number between 0 and 100.',
      speed: 'Speed: a number above 0.',
    },
    errors: {
      invalid: 'The form is not complete yet.',
      not_found: 'That does not exist any more.',
      too_many_requests: 'Too many reports in a short time — try again later.',
    },
  },

  // Context help: the small "?" in the head of every panel, a modal with two
  // to four sentences. `button` is the aria-label of the button itself; the
  // remaining pairs are the title and the text of the modal, per panel.
  help: {
    button: 'Help on {title}',
    layersTitle: 'Layers',
    layersBody:
      'A layer is one colour in the document, stacked in drawing order in the list — the top row lies at the back, the bottom one at the front. Every layer carries a role: engrave (fill) or cut (stroke). Layers can be renamed, grouped and merged. Two direct neighbours of one colour are one layer: when reordering or recolouring creates such a pair, the editor joins it in the same step; "Join same-colour neighbours" catches up on a document that arrived that way. If a layer takes the colour of another one that is not next to it, the editor offers to weld the two rather than doing it quietly.',
    colorsTitle: 'Colours',
    colorsBody:
      'One tile per colour that occurs in the document. “Merge similar ones” gathers colours that lie close together into one layer, using the slider — handy when a vectorisation produced several near-duplicates of a colour. “Only recolour” gives every affected layer the target colour without welding them.',
    pathTitle: 'Path',
    pathBody:
      '“Simplify” lowers the node count of the selected path through a tolerance slider and shows the effect as a preview on the canvas before it is applied — if an adjustment stays within the tolerance, the result does not change. “Smooth” softens the path’s transitions through a strength slider — the nodes stay put, only their rounding grows with it; here too the canvas shows a preview, and only letting go applies it.',
    arrangeTitle: 'Arrange',
    arrangeBody:
      'Aligning needs at least two paths, or works against the page; distributing needs at least three. “Stacking order” moves the selection through the stack: to the front or back of its own layer, while a single step crosses into the neighbouring layer at the edge — taking on its colour. Measurements and rotation act on the selection directly, “Duplicate in a pattern” repeats it in a circle or in a grid. The rulers, grid and snapping below are view aids only — they are not part of the document and create no undo step.',
    cleanupTitle: 'Clean up',
    cleanupBody:
      'Finds small parts under an adjustable edge length, nodes above the budget a cutter still reads reliably, and loose parts that would fall out as stencils of their own while cutting. Removing, optimising and welding each act on the visible layers (or only on the one set to solo) and are an undo step of their own.',
    shapeTitle: 'Shape',
    shapeBody:
      '“Unite”, “Subtract”, “Intersect”, “Exclude” and “Divide” combine several selected areas as boolean operations — open paths do not take part. “Outline…” offsets the selection outwards or inwards by a distance, either as a new layer under the source or as a replacement for the paths themselves.',
    rhinestoneTitle: 'Rhinestones',
    rhinestoneBody:
      'Turns the selection into a hole pattern for rhinestones: circles along the outline (corners first), as a grid inside the area, or both. The circles are the stencil’s holes — stone size plus play — and no hole ever overlaps another. “Apply” creates a new cut layer “Rhinestones”; the source stays.',
    sheetTitle: 'Sheet',
    sheetBody:
      'Packs the selection — or every visible path if nothing is marked — onto a mat size, saving space, with an optional 90° rotation. “Fill with copies” duplicates a motif until the mat is full or a chosen number is reached; both land together with the new page size in a single undo step.',
    rasterTitle: 'Bitmap',
    rasterBody:
      'The brush and the eraser paint either on the active layer (as a vector shape) or on the photo (as pixels) — the “Paint on” switch decides. The smart fill floods a connected or same-coloured area of the image and turns it into an outline in the active colour. While the crop tool is active, this panel also shows the measurements of the crop.',
    aiTitle: 'AI',
    aiBody:
      'Removes the background or doubles the resolution of the image — both run in the browser, without a server, and are Pro-only. The result replaces the working image as one undo step; “… and vectorise again” runs the vectorisation in the same move.',
    retraceTitle: 'Re-vectorise',
    retraceBody:
      'At first the sliders only act on a free preview — only “Re-vectorise” really replaces the vector layers, and it costs one of the day\'s slots. “Calculate three variants” compares half, equal and double the colour count side by side; “Touch up a region” vectorises only a dragged section again instead of the whole image.',
    referenceTitle: 'Reference',
    referenceBody:
      'Puts the original photo under the vector shapes as a transparent layer, so the outline can be compared with the image. The slider sets the opacity — 0 % hides the original image completely. The product mockups below show the silhouette of a mug, a T-shirt, a folded card or a glass at real size instead, so the document size can be matched to them.',
    exportTitle: 'Export',
    exportBody:
      'Pick a machine profile (Cricut, xTool, Brother ScanNCut) and a file format that fits it — SVG, DXF, PNG, PDF or FCM. “Weld the overlaps before exporting” unites overlapping shapes in every visible layer so that nothing is cut twice; afterwards you can write a cut report straight away.',
    layeringTitle: 'Layering',
    layeringBody:
      '“Trap” grows every layer by the chosen overlap underneath the layers above it — so no backing material flashes between two colours sitting side by side when the press shifts them by a tenth of a millimetre. “Knock out” does the opposite and takes out of every layer whatever the layers above already cover, all but that same narrow seam at the edge — it saves material and keeps the stack from building up thick.',
    historyTitle: 'History',
    historyBody:
      'Shows saved versions of the document with a time and a preview — the working image is not versioned along with it. A version can be viewed and restored without losing the current work: restoring keeps the present state as a version first.',
  },

  // Parametric patterns: geometry/patterns/*, PatternPanel.tsx.
  patterns: {
    title: 'Pattern',
    rectHint: 'With nothing selected a rectangle of the width and height below is used, centred on the sheet.',
    kind: 'Pattern',
    kindHoneycomb: 'Honeycomb',
    kindVoronoi: 'Voronoi',
    kindSpirograph: 'Spirograph',
    kindHoles: 'Random holes',
    width: 'Width',
    widthLabel: 'Width of the rectangle in millimetres',
    height: 'Height',
    heightLabel: 'Height of the rectangle in millimetres',
    cellSize: 'Cell size',
    cellSizeLabel: 'Width across the flats of a honeycomb cell, in millimetres',
    wall: 'Wall thickness',
    wallLabel: 'Wall thickness between the honeycomb cells, in millimetres',
    cellCount: 'Cells',
    cellCountLabel: 'Number of Voronoi cells',
    web: 'Minimum web',
    webLabel: 'Minimum web between the holes, in millimetres',
    seed: 'Random seed',
    seedLabel: 'Random seed of the pattern',
    reroll: 'Roll again',
    relax: 'Relaxed (more even cells)',
    bigRadius: 'Fixed circle R',
    bigRadiusLabel: 'Radius of the fixed circle in millimetres',
    smallRadius: 'Rolling circle r',
    smallRadiusLabel: 'Radius of the rolling circle in millimetres',
    penOffset: 'Pen offset d',
    penOffsetLabel: 'Distance of the pen from the centre of the rolling circle, in millimetres',
    turns: 'Turns',
    turnsLabel: 'Number of turns',
    spirographNote:
      'The spirograph is a line, not a hole: it always lands in a layer of its own with the cut role — for a pen plotter or for engraving.',
    radiusMin: 'Radius min',
    radiusMinLabel: 'Smallest hole radius in millimetres',
    radiusMax: 'Radius max',
    radiusMaxLabel: 'Largest hole radius in millimetres',
    density: 'Density (%)',
    densityLabel: 'Share of the area the holes aim for, in per cent',
    output: 'Result',
    outputHoles: 'As holes in the shape',
    outputLayer: 'As a layer of its own',
    layerName: 'Pattern',
    count: { one: '1 cell', other: '{count} cells' },
    densityShort: '— of the {wanted} the density asked for. For more: smaller radii, or a smaller web.',
    refusedCells: 'Too fine: {wanted} cells, at most {limit} are possible. Increase the cell size.',
    refusedNodes:
      'Too much: {nodes} nodes, at most {limit} are possible. Use a coarser pattern — larger cells, fewer holes, fewer turns.',
    budget: '{nodes} nodes — over the cut budget of {limit}. Use a coarser pattern, or “Optimise nodes” in the Cleanup panel.',
    apply: 'Apply pattern',
    empty: 'No pattern fits — the shape is too small for this cell size and web.',
    done: { one: 'Pattern applied — 1 cell', other: 'Pattern applied — {count} cells' },
    helpTitle: 'Pattern',
    helpBody:
      'Fills the selected closed shape with a parametric pattern of holes — honeycomb, Voronoi, spirograph or random holes. The cells are intersected with the shape and inset by the minimum web, so that enough material is left between every hole and towards the rim. “As holes in the shape” writes the cells into the shape itself as sub-paths, “As a layer of its own” puts them into a new layer called Pattern.',
  },

  // “Insert → Monogram…”: one to three initials become a mark
  // (geometry/monogram.ts, MonogramDialog.tsx). A group of its own rather
  // than entries in `insert` and `help`, so the generator stays one block
  // here too.
  monogram: {
    menu: 'Monogram…',
    title: 'Monogram',
    initials: 'Initials (1–3)',
    style: 'Style',
    styleClassic: 'Classic',
    styleCircle: 'Circle',
    styleDiamond: 'Diamond',
    styleSplit: 'Split letter',
    font: 'Font',
    width: 'Overall width (mm)',
    frame: 'Frame',
    ring: 'Frame width (mm)',
    band: 'Band height (mm)',
    bandAuto: 'automatic (18 %)',
    bandName: 'Name in the band',
    weld: 'Weld the letters',
    preview: 'Preview of the monogram',
    previewEmpty: 'Enter one to three initials.',
    create: 'Insert',
    cancel: 'Cancel',
    layerName: 'Monogram',
    invalidInitials: 'Please enter one to {max} initials.',
    invalidWidth: 'Please enter an overall width between {min} and {max} mm.',
    invalidRing: 'Please enter a frame width between {min} and {max} mm.',
    invalidBand: 'Please enter a band height between {min} and {max} mm.',
    nothing: 'This font has no outlines for these characters.',
    failed: 'The monogram could not be created.',
    done: 'Monogram inserted — it sits on top, on the “Monogram” layer.',
    hint: 'Classic sets the middle initial — the surname — larger and in the centre. Circle and diamond intersect the letters with the shape, split letter punches a horizontal band out of one large letter.',
    helpTitle: 'Monogram',
    helpBody:
      'A monogram is made here as plain paths on a new “Monogram” layer — all closed, scaled to the overall width you set and, if you want, welded into a single silhouette. Unlike text, no recipe stays on the layer: once inserted it is ordinary geometry and is worked on with the editor’s own tools.',
  },

  registrationMarks: {
    menu: 'Alignment marks…',
    title: 'Alignment marks',
    shape: 'Shape',
    shapeTriangle: 'Triangle',
    shapeCross: 'Cross',
    size: 'Size (mm)',
    gap: 'Distance from the motif (mm)',
    count: 'Positions',
    count2: '2 — top left, bottom right',
    count3: '3 corners',
    count4: '4 corners',
    target: 'Where',
    targetEachLayer: 'Into every colour layer',
    targetOwnLayer: 'As a layer of their own',
    layerName: 'Alignment marks',
    hint: 'Place the marks last — after “Arrange” and “Arrange by colour”. The sheet packer leaves them alone, but the motif moves out from under them.',
    invalidSize: 'The size has to be between {min} and {max} mm.',
    invalidGap: 'The distance has to be between {min} and {max} mm.',
    empty: 'No visible layer with anything on it — there is nothing for the marks to sit around.',
    cancel: 'Cancel',
    create: 'Insert',
    remove: 'Remove alignment marks',
    done: {
      one: '1 alignment mark placed.',
      other: '{count} alignment marks placed.',
    },
    removed: {
      one: '1 alignment mark removed.',
      other: '{count} alignment marks removed.',
    },
    helpTitle: 'Alignment marks',
    helpBody:
      'Alignment marks are small triangles or crosses that sit at exactly the same spot in every colour layer — lay the cut layers mark on mark and the motif lines up with them. Inserting them again replaces the ones that are there, and “Remove alignment marks” takes them back out of every layer, even after they have been moved.',
  },

  // "Insert → Finger joint box…": a box for the laser, cut and slotted
  // together (geometry/fingerJointBox.ts). Not the same thing as "Box…",
  // which is folded from paper — this one is wood or acrylic, which is why it
  // asks for material thickness and kerf instead of fold lines.
  fingerJoint: {
    menu: 'Finger joint box…',
    title: 'Finger joint box',
    length: 'Length (mm)',
    width: 'Width (mm)',
    height: 'Height (mm)',
    measure: 'Measurements are',
    measureOuter: 'Outside',
    measureInner: 'Inside',
    thickness: 'Material thickness (mm)',
    finger: 'Finger width (mm)',
    fingerAuto: 'automatic (twice the material thickness)',
    lid: 'Lid',
    lidClosed: 'closed (6 parts)',
    lidOpen: 'open (5 parts)',
    kerf: 'Kerf (mm)',
    summary: 'Outside {length} × {width} × {height} mm · {parts} parts · {fingers} fingers per long edge · sheet {sheetWidth} × {sheetHeight} mm',
    create: 'Create',
    cancel: 'Cancel',
    layerName: 'Finger joint box',
    invalidSize: 'Please enter measurements between {min} and {max} mm.',
    invalidThickness: 'Please enter a material thickness between {min} and {max} mm.',
    invalidFinger: 'Please enter a finger width between {min} and {max} mm — or leave the field empty for “automatic”.',
    invalidKerf: 'Please enter a kerf between 0 and {max} mm.',
    tooFlat: 'Too flat: the base and the lid leave no height for the fingers. Make it taller or pick thinner material.',
    tooFine: 'The fingers would be narrower than the material is thick — they break while you assemble the box. Pick wider fingers.',
    done: 'Finger joint box created — {parts} parts in one line layer.',
    hint: 'Made for the laser: every part lies flat next to the others, the edges are complementary and slot together rather than being glued. The kerf makes up for what the beam burns away — usually 0.1 to 0.2 mm in 3 mm plywood. For folding paper, use “Box…” instead.',
  },

  // "Insert → Bend slots…" (a living hinge): a field of short cuts that makes
  // a stiff panel bend in one place (geometry/livingHinge.ts).
  livingHinge: {
    menu: 'Bend slots…',
    title: 'Bend slots',
    width: 'Field width (mm)',
    height: 'Field height (mm)',
    slot: 'Slot length (mm)',
    bridge: 'Bridge (mm)',
    rowGap: 'Row spacing (mm)',
    stagger: 'Staggered (brick pattern)',
    frame: 'Frame around the field',
    summary: '{rows} rows · {slots} slots · field {width} × {height} mm',
    create: 'Create',
    cancel: 'Cancel',
    layerName: 'Bend slots',
    invalidField: 'Please enter a field size between {min} and {max} mm.',
    invalidSlot: 'Please enter a slot length between {min} and {max} mm.',
    invalidBridge: 'Please enter a bridge between {min} and {max} mm.',
    invalidRowGap: 'Please enter a row spacing between {min} and {max} mm.',
    tooSmall: 'Not a single slot fits into this field: a slot plus its bridges is taller than the field itself.',
    done: '{slots} bend slots created — in one line layer.',
    hint: 'The slots run vertically; the panel bends around the vertical axis. The shorter the bridge and the tighter the rows, the softer the curve — and the sooner it snaps. Staggered rows spread the twist over twice as many bridges. No slot ever touches the edge.',
  },

  // "Insert → Shadow box…": a motif cut into n sheets of paper standing
  // behind one another in a frame (raster/shadowbox.ts for the image work,
  // geometry/shadowbox.ts for the cut). A group of its own rather than an
  // entry under `insert`, so this tool keeps its text in one place — from the
  // menu entry to the context help.
  shadowbox: {
    menu: 'Shadow box…',
    title: 'Shadow box',
    layers: 'Layers',
    frame: 'Frame width (mm)',
    web: 'Minimum web (mm)',
    cumulative: 'Cumulative (a relief rather than separate bands)',
    preview: 'Preview',
    previewPending: 'Calculating …',
    previewEmpty: 'Nothing to show yet.',
    previewCaption: 'The layers front to back, stacked.',
    create: 'Insert',
    cancel: 'Cancel',
    layerFront: 'Layer {index} (front)',
    layerMiddle: 'Layer {index}',
    layerBack: 'Layer {index} (back)',
    invalidLayers: 'Please enter a layer count between {min} and {max}.',
    invalidFrame: 'Please enter a frame width between {min} and {max} mm.',
    invalidWeb: 'Please enter a minimum web between {min} and {max} mm.',
    noImage: 'This project has no working image — a shadow box is calculated from the image.',
    failed: 'The shadow box could not be calculated.',
    done: '{layers} layers inserted — “Layer 1” sits at the front.',
    doneIslands: '{layers} layers inserted; {islands} loose pieces with no connection to the frame were left out.',
    helpTitle: 'Shadow box',
    helpBody:
      'Splits the working image into several sheets of paper by brightness: each layer is the frame with the windows you look through onto the next one, and because dark areas are open in more layers, a relief appears. The thresholds sit on the quantiles of the brightness so that every layer opens roughly the same amount; pieces that no longer hang on the frame are left out and counted. Tip: mount the finished layers behind one another with 2–3 mm spacers — without a gap there is no shadow, and without a shadow no depth.',
  },

  grayscale: {
    menu: 'Grayscale for laser engraving…',
    exportLink: 'Grayscale for laser engraving …',
    exportHint: 'Photo and layers as one grayscale PNG for raster engraving.',
    title: 'Grayscale for laser engraving',
    preview: 'Engraving preview',
    previewPending: 'Working …',
    previewFailed: 'The artboard could not be drawn.',
    brightness: 'Brightness',
    contrast: 'Contrast',
    gamma: 'Gamma',
    invert: 'Invert (for glass, slate, anodised aluminium)',
    removeBackground: 'Remove background (cut out the subject with AI, lettering stays, transparent PNG)',
    removingBackground: 'Removing the background … {progress}',
    removeBackgroundFailed: 'The background could not be removed — the image stays whole, only empty areas are transparent.',
    reset: 'Reset',
    dpi: 'Resolution',
    dpiOption: '{dpi} DPI · {width} × {height} px',
    dpiTooLarge: '{dpi} DPI · too large for the browser',
    download: 'Download as PNG',
    apply: 'Use as working image',
    proOnly: 'Downloading and using the image are part of Pro. You can try the preview.',
    noImage: 'This project has no working image — the grayscale image can only be downloaded.',
    otherArtboard: 'The working image belongs to the first artboard — it can only be replaced there; downloading works everywhere.',
    downloaded: 'Grayscale PNG saved at {dpi} DPI.',
    applied: 'The grayscale image is now the working image; the layers are hidden.',
    failed: 'The grayscale image could not be made.',
    helpTitle: 'Grayscale for laser engraving',
    helpBody:
      'Combines the working image and every visible layer of the active artboard into one grayscale image. A laser reads grey as power: black burns fully, white not at all — so empty areas stay white. Brightness, contrast and gamma set how dark the engraving comes out on your material; use "Invert" for glass, slate or anodised aluminium, where the laser makes things lighter instead of darker. The PNG carries the chosen resolution, so LightBurn and xTool Creative Space take over the size in millimetres. 318 DPI (≈ 0.08 mm line spacing) is a good start for wood. "Use as working image" replaces the working image and hides the layers — one undo brings everything back.',
  },

  // Fonts kept in the account rather than only on this device — see de.ts.
  accountFonts: {
    group: 'From your account',
    badge: 'Account',
    loading: 'Fetching the font from your account…',
    notUsable: 'WOFF2 — not as paths',
    manage: 'Manage the fonts in your account',
    added: 'Font “{font}” loaded from your account.',
    errorOffline:
      'The font “{font}” is in your account, but you are offline right now. It will work once you are back online.',
    errorDenied: '“{font}” needs you signed in — sign in again and try once more.',
    errorGone: 'The font “{font}” is no longer in your account.',
    errorMismatch: 'The font “{font}” arrived damaged and was not stored.',
    errorUnreadable: 'The font “{font}” could not be fetched from your account just now.',
    stored: {
      one: '1 font added and stored in your account — it is ready in every project.',
      other: '{count} fonts added and stored in your account — they are ready in every project.',
    },
    uploadOffline:
      '“{font}” is on this device, but you are offline — it reaches your account once you add it again with a connection or upload it under “Account → Your own fonts”.',
    uploadDenied: '“{font}” is on this device but did not reach your account — sign in again and add it once more.',
    uploadThrottled:
      '“{font}” is on this device but did not reach your account: too many uploads this hour. Add it again later or upload it under “Account → Your own fonts”.',
    uploadRejected: '“{font}” is on this device but did not reach your account just now.',
    uploadRejectedWhy: '“{font}” is on this device but did not reach your account: {detail}',
  },

  customFonts: {
    group: 'Your own',
    add: 'Add your own font…',
    reading: 'Reading the font…',
    formats: 'TTF, OTF or WOFF — the editor cannot read WOFF2. At most 5 MB per file; several at once are fine.',
    license:
      'Only use fonts whose licence allows making cut files — Schnittklar checks no licences. Added here, the file stays on this device.',
    licenseAccount:
      'Only use fonts whose licence allows making cut files — Schnittklar checks no licences. Added here, the file goes into your account and is ready in every project and on every device you are signed in on.',
    remove: 'Remove {font}',
    removed: '{font} removed',
    added: { one: '1 font added', other: '{count} fonts added' },
    missing:
      'The font “{font}” is not on this device. Add it — with Pro it goes into your account as you do, so it is there on every device — or pick another one.',
    missingOption: 'Not on this device',
    errorTooLarge: '“{file}” is too large — at most {max} MB per font file.',
    errorWoff2: '“{file}” is a WOFF2 file. The editor cannot read those — use TTF, OTF or WOFF.',
    errorUnsupported: '“{file}” is not a font file the editor can read — use TTF, OTF or WOFF.',
    errorUnreadable: '“{file}” could not be read — the file is damaged or incomplete.',
    errorStore: '“{file}” could not be stored on this device.',
    helpTitle: 'Your own fonts',
    helpBody:
      'Font files added here are read in the browser and stored on this device. With Pro the editor also puts every added font into your account — as many as you like, at most 5 MB per file: the text dialog then offers it in every project and on every device you are signed in on, and the file goes to nobody else, not even when you publish the project. Without Pro the file stays on this device. For somebody opening your template the letters you have already set stay as paths; only setting the text again needs a font of their own.',
  },

  // Kerf compensation in the export panel — see de.ts.
  kerf: {
    label: 'Kerf (cut width)',
    hint: 'Typically 0.1–0.2 mm on lasers; leave it at 0 for blade plotters. Outer contours grow by half of it, holes shrink by half; open paths stay as they are. If the document tells cut and engrave apart, only the cut is compensated.',
    invalid: 'Please enter a number between 0 and 1 mm.',
    zipHint: 'Applies to the file export above; the ZIP exports and Print & Cut go out without compensation.',
    badge: 'Kerf {mm} applied.',
    historyLabel: 'Kerf {mm}',
  },

  // The personal element library: keep a selection under a name and put it
  // back into any project of your own.
  library: {
    menu: 'Library…',
    title: 'Element library',
    save: 'Save to library',
    saveTitle: 'Save to library',
    saveLabel: 'Name',
    saveConfirm: 'Save',
    saved: '“{name}” is in your library now.',
    saveEmpty: 'Select what you want to save first.',
    saveNoName: 'Without a name you will not find it again — please type one.',
    saveFailed: 'The element could not be saved.',
    saveLimit: 'Your library is full ({limit} elements). Delete one to make room.',
    search: 'Search elements…',
    loading: 'Loading the library…',
    loadFailed: 'The library could not be loaded.',
    offline: 'You are offline — your library lives on the server and cannot be reached right now.',
    empty: 'No elements yet. Select something and save it with “Save to library”.',
    noMatches: 'No element matches your search.',
    size: '{width} × {height} mm',
    rename: 'Rename',
    renameTitle: 'Rename element',
    renameLabel: 'Name',
    renameConfirm: 'Rename',
    renameFailed: 'Renaming did not work.',
    delete: 'Delete',
    deleteTitle: 'Delete “{name}”?',
    deleteBody: 'The element leaves your library. Copies you have already inserted stay where they are.',
    deleteConfirm: 'Delete',
    deleted: 'Element deleted.',
    deleteFailed: 'Deleting did not work.',
    nothing: 'This element has no paths.',
    done: { one: '1 path inserted.', other: '{count} paths inserted.' },
    undoLabel: 'Insert element “{name}”',
    close: 'Close',
    helpTitle: 'Element library',
    helpBody:
      'This is where the cut-outs you kept live: a selection becomes an element with a name, its measurements and a preview through “Save to library” in the Arrange panel. Clicking a tile puts the element into the middle of the view — layers of the same colour and role join the existing layer, everything else arrives as a new one.',
  },
  guest: {
    pick: 'Choose an image',
    dropHint: 'Drag an image here or pick one — PNG, JPG, HEIC or PDF, 20 MB at most.',
    tracing: 'Vectorising …',
    tracingHint: 'This takes a few seconds. Leave the page open.',
    resultHeading: 'Done',
    layers: { one: '1 layer', other: '{count} layers' },
    size: '{width} × {height} mm',
    download: 'Download SVG',
    downloadHint: 'The SVG opens in Design Space, Silhouette Studio (Designer Edition) and Inkscape.',
    again: 'Try another image',
    empty: 'No surface came out of this image. A motif with clear colours and little background works better.',
    lost: 'There is no result here right now. Upload an image and it will be.',
    accountHeading: 'Carry on with it',
    accountBody:
      'Your result lives in this browser. With an account it moves across on its own the first time you sign in — image and all — and then it carries on where this stops: merge layers, smooth paths, vectorise again, and five files a day instead of one.',
    accountExtras: 'Pro adds DXF, PNG, PDF and FCM, larger uploads and batch processing.',
    accountCta: 'Create an account',
    accountSignIn: 'I already have one',
    errors: {
      tooLarge: 'The file is too large — 20 MB at most.',
      unsupported: 'That is not an image we can work with. Try a PNG, JPG, HEIC or a PDF.',
      limit: 'You have used your free attempt. An account takes it from here — five files a day, free.',
      busy: 'It is busy right now. Try again in a minute.',
      timeout: 'The image took too long. A smaller or simpler motif is quicker.',
      failed: 'That did not work. Try another image.',
      offline: 'No connection. It will work once you are back online.',
    },
    imported: { one: 'Your result is in your projects now.', other: '{count} results are in your projects now.' },
  },

  fileCheck: {
    pick: 'Choose an SVG',
    dropHint: 'Drag your SVG file here or pick one. It stays in your browser — it does not come to us.',
    reading: 'Reading …',
    clean: 'There is nothing wrong with this file. It should cut cleanly.',
    findingsHeading: 'What we found',
    repairsHeading: 'What we can repair',
    resultHeading: 'Repaired',
    download: 'Download the repaired SVG',
    again: 'Check another file',
    summary: '{paths} paths on {layers} layers',
    accountHeading: 'Carry on instead of only repairing',
    accountBody:
      'With an account the repaired file lands in the editor as a project: merge layers, set the measurements, export for your machine.',
    accountCta: 'Create an account',
    errors: {
      read: 'The file could not be read.',
      invalid: 'That is not an SVG file we can read.',
      empty: 'There is no geometry in this file to build a cut line from.',
      tooManyNodes: 'This file has more nodes than the editor handles.',
      tooLarge: 'The file is too large — 20 MB at most.',
    },
  },

  fileLaunch: {
    idleHeading: 'No file arrived',
    idleInstalled:
      'Pick a file in your file manager, choose “Open with” and then Schnittklar — it becomes a new project right here.',
    idleBrowser:
      'Opening files directly works in the installed app. In the browser you upload them in your dashboard — same route, same result.',

    uploading: 'Uploading “{name}” …',
    tracing: 'Vectorising “{name}” …',
    tracingHint: 'This takes a few seconds. Leave the page open.',
    done: '“{name}” is ready — opening the project.',
    ignored: {
      one: 'One more file was left behind — upload it in your dashboard.',
      other: '{count} more files were left behind — upload them in your dashboard.',
    },

    signInHeading: 'This needs an account',
    signInBody:
      '“{name}” is waiting on this device. Sign in and we create the project straight away — you will not have to find the file again.',
    signInCta: 'Sign in',
    signUpCta: 'Create an account',
    signUpHint: 'No account yet? The file stays here either way, until you sign in for the first time.',

    offlineHeading: 'No connection',
    offlineBody:
      'Vectorising happens on the server, and it cannot be reached right now. “{name}” is waiting on this device — as soon as you are back online it carries on by itself.',
    offlineWaiting: 'Waiting for the connection …',

    retry: 'Try again',
    dashboard: 'Go to my projects',
    upgrade: 'Go Pro',

    errors: {
      unsupported: 'We cannot do anything with that. Schnittklar opens PNG, JPEG, WebP, HEIC, AVIF, PDF and EPS.',
      tooLarge: 'The file is too large — 20 MB at most.',
      limitReached: 'Daily limit reached ({used} / {limit}).',
      limitReachedPlain: 'Daily limit reached.',
      uploadFailed: 'The file could not be uploaded.',
      conversionFailed: 'The vectorisation failed. Try another file.',
      lostTrack: 'The status of the vectorisation is no longer available. Reload the page.',
  },

    },
  layerBulk: {
    title: 'Several layers',
    hint: 'Ctrl- or shift-click to tick several layers. Every action here runs over all of them — and is a single step in the history.',
    selected: { one: '1 layer ticked', other: '{count} layers ticked' },
    activeOnly: 'The active layer only',
    clear: 'Clear the ticks',
    smooth: 'Smooth',
    round: 'Round corners',
    radius: 'Radius in mm',
    removeSmall: 'Remove small parts',
    smallEdge: 'Smaller than (mm)',
    lineStyle: 'Set the line style',
    apply: 'Set',
    nothing: 'There was nothing to do.',
    donePaths: { one: '1 path changed, on {layers} layers.', other: '{count} paths changed, on {layers} layers.' },
    doneRemoved: { one: '1 small part removed.', other: '{count} small parts removed.' },
    doneLayers: { one: '1 layer changed.', other: '{count} layers changed.' },
    skippedPaths: { one: '1 path was skipped.', other: '{count} paths were skipped.' },
    skippedLayers: { one: '1 layer was skipped.', other: '{count} layers were skipped.' },
  },
}
